import { CheckCircle2, ChevronDown, Download, FileText } from 'lucide-react'

import { cn } from '../../utils/cn'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'

export type CodifiedPlanRuleTable = {
  columns: string[]
  rows: string[][]
}

export type CodifiedPlanRule = {
  id: string
  title: string
  summary: string
  details: string[]
  table?: CodifiedPlanRuleTable
  sources: string[]
}

type CodifiedPlanRuleGroup = {
  id: 'goal-inputs' | 'timeline-targets' | 'weekly-generation' | 'structure-support' | 'milestones' | 'adaptation-mutation'
  title: string
  description: string
  rules: CodifiedPlanRule[]
}

type CodifiedPlanGlossaryTerm = {
  term: string
  definition: string
}

export const CODIFIED_PLAN_GLOSSARY_TERMS: CodifiedPlanGlossaryTerm[] = [
  {
    term: 'Training day',
    definition: 'A scheduled day that carries the main weekly work, such as distance, elevation, pack, HIIT, or milestone work. Training days count against total training-day caps; there is no separate primary-training-day cap.'
  },
  {
    term: 'Support day',
    definition: 'A lighter training day used for supporting work such as strength, mobility, balance, or recovery. Support days count as training days but are removed before primary training days when structure remediation needs more rest.'
  },
  {
    term: 'Rest day',
    definition: 'A day with no scheduled workout after structure planning and remediation. Rest days satisfy phase minimum rest requirements and can be created by converting eligible support or primary training days.'
  },
  {
    term: 'Milestone',
    definition: 'A protected goal-specific workout, usually a long hike or peak simulation, that can replace normal generation for that date and is preserved during most structure cleanup.'
  },
  {
    term: 'Hiking Fitness',
    definition: 'A fitness-level goal that resolves to a linked or synthesized hike target, then uses the regular hiking-plan generator.'
  },
  {
    term: 'Proxy goal',
    definition: 'A real hike used as a training target without representing an actual upcoming trip.'
  },
  {
    term: 'True elevation',
    definition: 'Outdoor or real-world vertical training access, such as stairs, hills, hill repeats, step-ups, local hikes, or trails.'
  },
  {
    term: 'Artificial elevation',
    definition: 'Machine-based vertical training access, such as a stair machine or treadmill incline.'
  },
  {
    term: 'Phase',
    definition: 'The current block of the plan timeline: PreBase, Base, Build, Peak, or Taper. Phase controls weekly target shaping and structure caps.'
  },
  {
    term: 'Remediation',
    definition: 'The planner pass that fixes a generated week when it violates structure rules, usually by converting eligible support or primary training days to rest.'
  }
]

export const GOAL_INPUT_RULES: CodifiedPlanRule[] = [
  {
    id: 'goal.resolve.type',
    title: 'Goal type selects the resolver path.',
    summary: 'The request goal type is not advisory; it chooses the data source and normalization behavior before any training math runs.',
    details: [
      'PopularHike loads a curated hike by id from PopularHikes.',
      'TourOperatorHike searches embedded tours inside TourOperators.',
      'Hiking-fitness/GeneralFitness levels resolve to their linked PopularHike when configured, or to a synthesized one-day hike from configured totals when no linked hike exists.',
      'General Hiking Fitness has five admin-editable levels: Couch to Trail, Easy, Moderate, Hard, and Strenuous. Their default hikes are Yosemite Valley Floor Loop, the shortened central Shenandoah section, Otter Trail, the W Trek, and the classic Tour du Mont Blanc circuit, respectively.',
      'After resolution, hiking-fitness goals use the same timeline, target, milestone, taper, and workout assembly rules as regular hiking plans.',
      'CustomHike first tries a CustomHikes document id, then falls back to the user saved custom planned hike.'
    ],
    sources: [
      'mobile/app/onboarding/choose-goal.tsx:326-390',
      'backend/Controllers/MobileTrainingPlanController.cs:397-422',
      'backend/Services/TrainingPlans/GoalHikeResolver.cs:34-272',
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs:160-756',
      'backend/Models/GeneralFitnessHikeDefaults.cs',
      'backend/Controllers/FitnessHikeTargetsController.cs',
      'dashboard/src/pages/FitnessHikeTargetsPage.tsx'
    ]
  },
  {
    id: 'goal.measurement-source',
    title: 'Curated goal values retain their metric or imperial source system.',
    summary: 'Unitless highest points and pack weights use persisted source metadata instead of being guessed from the raw number alone.',
    details: [
      'Explicit ft, feet, m, lb, and kg suffixes remain authoritative.',
      'When a curated value has no suffix, SourceMeasurementUnit selects feet/pounds for Imperial and meters/kilograms for Metric.',
      'Legacy curated pack weights without source metadata continue to assume pounds for backward compatibility.',
      'The explicit AboveEightThousandFeet flag remains a lower bound when the highest-point value is missing or lower.'
    ],
    sources: [
      'backend/Services/TrainingPlans/GoalHikeResolver.cs',
      'backend/Services/TrainingPlans/TrainingPlanPackWeightNormalizer.cs',
      'backend/Services/HikeDifficultyCalculator.cs',
      'dashboard/src/pages/TourManagementPage.tsx'
    ]
  },
  {
    id: 'goal.popular.itinerary-synthesis',
    title: 'Popular hikes can synthesize a one-day itinerary from totals.',
    summary: 'Sparse popular-hike data does not always fail; totals may become a one-day itinerary.',
    details: [
      'If a popular hike has no itinerary but has total distance, a one-day itinerary is synthesized from total distance/elevation/loss.',
      'A warning is emitted when itinerary data is synthesized.',
      'If neither itinerary nor totals are available, the goal resolves with a warning and downstream target generation falls back to default plan targets.'
    ],
    sources: ['backend/Services/TrainingPlans/GoalHikeResolver.cs:63-110']
  },
  {
    id: 'goal.no-itinerary-default-targets',
    title: 'Missing itinerary data falls back to default goal-hike targets.',
    summary: 'When a goal-hike has no itinerary, the generator uses fixed totals rather than failing target computation.',
    details: [
      'Default total distance is about 62 miles (100.0 km).',
      'Default total elevation gain is about 9,843 ft (3,000.0 m).',
      'Target pack weight uses the request override when present; otherwise it uses the resolved goal pack weight.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanTargetPolicyPlanner.cs:59-67']
  },
  {
    id: 'goal.pack.defaults',
    title: 'Goal pack weight text is normalized for storage.',
    summary: 'Pack text on curated goals is normalized before weekly pack ramp rules run.',
    details: [
      'Blank pack text defaults to about 20 lb (9.0 kg).',
      'Ultralight or ultra-light maps to about 10 lb (4.5 kg); light maps to about 15 lb (6.8 kg); traditional maps to about 25 lb (11.3 kg); heavy maps to about 30 lb (13.6 kg).',
      'Numeric strings with lb/lbs/pounds are converted to kg; numeric strings with kg/kilograms stay kg.',
      'Bare numeric curated goal sources are assumed to be pounds; bare numeric custom sources follow the user measurement unit when available.',
      'Custom planned hikes use request override, saved onboarding pack weight, or no pack load depending on the data path.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanPackWeightNormalizer.cs', 'backend/Services/TrainingPlans/GoalHikeResolver.cs']
  },
  {
    id: 'goal.pack.normalized',
    title: 'Goal pack normalization is traced before pack progression.',
    summary: 'Pack diagnostics show source text, assumed units, raw kg, canonical target, and generated max workout pack.',
    details: [
      'The generator records the normalized source kg before target planning and pack progression use it.',
      'A final diagnostic records source text, explicit or assumed unit, raw resolved kg, canonical plan target after intentional caps, generated max workout pack after assignment rounding, and the resolution path.',
      'Canonical target and generated max can differ when plan caps, taper progression, or assignment rounding keep actual workouts below the source pack value.',
      'Curated bare numeric sources are diagnostic-only assumptions and do not add user-facing warnings.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanGenerator.cs', 'backend/Models/TrainingPlans/ResolvedPackWeight.cs']
  },
  {
    id: 'goal.altitude.parse',
    title: 'Goal altitude controls HIIT and high-altitude multipliers.',
    summary: 'Highest-point text and above-8000-ft flags become maxAltitudeMeters before target and HIIT rules run.',
    details: [
      'An explicit above-8000-ft flag returns about 8,327 ft (2,538 m), just above the HIIT threshold.',
      'Highest-point strings are parsed for the first number; ft/feet values are accepted directly, then normalized for backend storage.',
      'Unparseable or blank altitude text resolves to 0 ft (0 m).'
    ],
    sources: ['backend/Services/TrainingPlans/GoalHikeResolver.cs:429-455']
  },
  {
    id: 'exercise.active-only',
    title: 'Plan generation can only use active exercise definitions.',
    summary: 'The generator loads the exercise catalog through an active-only filter before accessibility filtering and workout selection.',
    details: [
      'The main generator calls GetByFilterAsync(new ExerciseFilter { IsActive = true }).',
      'Exercise selection helpers also check exercise.IsActive before choosing preferred or fallback definitions.',
      'Generation, repair, and target-sync decisions use active Base Camp catalog codes directly rather than unmatched compatibility aliases.'
    ],
    sources: [
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs:167-168',
      'backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs:443-467'
    ]
  },
  {
    id: 'equipment.access-filter',
    title: 'User access gates every generated exercise prescription.',
    summary: 'An exercise that requires unselected equipment or true-elevation access cannot appear anywhere in the generated plan.',
    details: [
      'True-elevation requirements are keyed by exercise code: stairs, hills/hill repeats, step-ups, local hikes/trails, and HIIT variants.',
      'Artificial equipment requirements are keyed by exercise code: stair machine, treadmill, and their active HIIT variants.',
      'Exercises not in the requirement map have no equipment gate and remain available.',
      'The filtered exercise list is used by normal workout assembly, milestone selection, support repair, semantic cleanup, and final target synchronization.',
      'After independent workout-card splitting, a final invariant rejects the entire generated plan if any gated exercise escaped the user access filter.'
    ],
    sources: [
      'backend/Services/TrainingPlans/TrainingPlanUserEquipmentAccess.cs',
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs',
      'backend/Services/TrainingPlans/TrainingPlanExerciseTargetSynchronizer.cs'
    ]
  }
]

export const TIMELINE_TARGET_RULES: CodifiedPlanRule[] = [
  {
    id: 'timeline.start-date',
    title: 'Plans start on the request creation date.',
    summary: 'The timeline starts at PlanCreationDate.Date, not the next Monday.',
    details: [
      'The first calendar week may be partial if PlanCreationDate is mid-week.',
      'The next full-week boundary is computed from the number of days remaining through Sunday.',
      'Older docs that say goal-hike plans start next Monday are outdated relative to the current builder.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanTimelineBuilder.cs:72-75']
  },
  {
    id: 'timeline.first-week-proration',
    title: 'The first week is prorated only when preferred days are insufficient.',
    summary: 'A mid-week start does not automatically add an extra week; it does so only when the remaining preferred days cannot satisfy trainingDaysPerWeek.',
    details: [
      'The builder counts preferred days remaining in the partial first week.',
      'If available preferred days are fewer than onboarding.TrainingDaysPerWeek, HasProratedFirstWeek is true.',
      'When prorated, the timeline adds one week to preserve the requested number of full weeks after the partial week.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanTimelineBuilder.cs:72-93', 'backend/Services/TrainingPlans/TrainingPlanTimelineBuilder.cs:145-175']
  },
  {
    id: 'timeline.goal-hike-duration',
    title: 'Goal-hike duration is anchored to the target hike date when present.',
    summary: 'With a target hike date, duration is computed from the date span between plan start and hike date.',
    details: [
      'The target hike date is read from request.GoalHikeStartDate.',
      'When the hike date is available, total weeks are based on the calendar distance to the hike date.',
      'The timeline emits warnings when the resulting plan is below the configured minimum recommended length.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanTimelineBuilder.cs:116-141']
  },
  {
    id: 'timeline.default-duration',
    title: 'Goal-hike plans without a hike date use the configured default duration.',
    summary: 'When no hike date is available, duration is CustomDurationWeeks or config.DefaultPlanDurationWeeks.',
    details: [
      'DefaultPlanDurationWeeks is 12 in PlanGenerationConfig.',
      'MinimumRecommendedWeeks is 8 and is used for warnings, not as a hard floor.',
      'If the first week is prorated, the partial week is added before the nominal duration.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanTimelineBuilder.cs:145-157', 'backend/Models/TrainingPlans/PlanGenerationConfig.cs:182-193']
  },
  {
    id: 'timeline.standard-phases',
    title: 'The canonical 12-week goal-hike plan is 4 Base, 4 Build, 2 Peak, 2 Taper.',
    summary: 'Exactly 12-week goal-hike plans use a fixed phase table.',
    details: [
      'Weeks 1-4 are Base.',
      'Weeks 5-8 are Build.',
      'Weeks 9-10 are Peak.',
      'Weeks 11-12 are Taper.'
    ],
    table: {
      columns: ['Phase', 'Weeks'],
      rows: [
        ['Base', '4'],
        ['Build', '4'],
        ['Peak', '2'],
        ['Taper', '2']
      ]
    },
    sources: ['backend/Services/TrainingPlans/TrainingPlanTimelineBuilder.cs:57-63', 'backend/Services/TrainingPlans/TrainingPlanTimelineBuilder.cs:178-185']
  },
  {
    id: 'timeline.prebase',
    title: 'Longer goal-hike plans prepend PreBase before the final 12 weeks.',
    summary: 'Plans longer than 12 weeks do not stretch the 4-4-2-2 sequence; extra weeks become PreBase.',
    details: [
      'PreBaseWeeks equals totalWeeks minus 12.',
      'The standard Base, Build, Peak, and Taper phase counts remain 4, 4, 2, and 2.',
      'PreBase is used by target shaping, cohort minimums, pack progression, and structure caps.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanTimelineBuilder.cs:187-190', 'backend/Services/TrainingPlans/TrainingPlanTimelineBuilder.cs:33-52']
  },
  {
    id: 'timeline.shortened-plan',
    title: 'Short plans retain the final weeks of the 12-week schedule.',
    summary: 'A 9-week plan uses canonical weeks 4–12; earlier training volume is omitted instead of compressed.',
    details: [
      'Phases are selected from the tail of the fixed 4 Base / 4 Build / 2 Peak / 2 Taper schedule. Nine weeks means 1 Base, 4 Build, 2 Peak, and 2 Taper.',
      'Distance and elevation keep the 12-week denominator and the retained curve factors. Omitted weeks do not become catch-up volume or target debt.',
      'Pack progression uses the canonical week, while displayed week numbers remain 1 through the actual plan duration. Exercise eligibility and earned support progression retain their existing rules.',
      'Partial first weeks keep their existing calendar proration. A partial starter plus nine full weeks uses part of canonical week 3 followed by weeks 4–12.',
      'Existing distance ceilings, optional TTL controls, workout limits, milestone safety rules, and taper protections remain active. Workout placement may differ after those rules.',
      'Plans of 12 weeks or longer retain their existing behavior. Existing saved plans change only when regenerated.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanTimelineBuilder.cs', 'backend/Services/TrainingPlans/TrainingPlanWeekTargetBuilder.cs', 'backend/Services/TrainingPlans/TrainingPlanGenerator.cs']
  },
  {
    id: 'timeline.preferred-days-default',
    title: 'Preferred training days default to Monday, Wednesday, Friday, and Saturday.',
    summary: 'When onboarding has no parseable preferred days, the generator uses the canonical four-day pattern.',
    details: [
      'Preferred day strings are parsed case-insensitively into System.DayOfWeek.',
      'If no valid preferred days are supplied, the fallback is Monday, Wednesday, Friday, Saturday.',
      'The same default is used by timeline proration and workout day selection.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanTimelineBuilder.cs:242-273', 'backend/Services/TrainingPlans/TrainingPlanGenerator.cs:1079-1109']
  },
  {
    id: 'target.top-days',
    title: 'Goal-hike total volume comes from the hardest itinerary days.',
    summary: 'The generator does not simply sum the full trip; it uses top distance and elevation days as base signals.',
    details: [
      'If the itinerary has fewer than 4 days, the whole itinerary is used for both distance and elevation bases.',
      'Otherwise the top config.TopDistanceDaysCount distance days are summed for base distance.',
      'The top config.TopElevationDaysCount elevation days are summed for base elevation.',
      'Both defaults are 4 days.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanTargetPolicyPlanner.cs:70-95', 'backend/Models/TrainingPlans/PlanGenerationConfig.cs:199-209']
  },
  {
    id: 'target.difficulty-level',
    title: 'Four fitness bands align hiking and endurance from least to most active.',
    summary: 'The less-active of the two four-band answers selects the multiplier row.',
    details: [
      'Hiking levels: FewTimes or NotAtAll = 1, OneThreeDaysMonth = 2, OneTwoDays = 3, ThreePlusDays = 4.',
      'Endurance levels: Occasionally or NotAtAll = 1, OneDayOrLess = 2, TwoThreeDays = 3, FourPlusDays = 4.',
      'Missing onboarding values default to level 2.',
      'The resolved level is Math.Min(endurance level, hiking level), so the less-active answer wins.',
      'When Base Camp saves a changed endurance or hiking frequency for a generated plan, the admin flow confirms regeneration and preserves completed workout history.'
    ],
    sources: [
      'backend/Services/TrainingPlans/TrainingPlanTargetPolicyPlanner.cs:215-278',
      'backend/Services/TrainingPlans/TrainingPlanRegenerationUtilities.cs',
      'dashboard/src/pages/MobileUserDetailPage.tsx'
    ]
  },
  {
    id: 'target.difficulty-multipliers',
    title: 'Four explicit multiplier rows scale the hardest-day base into plan volume.',
    summary: 'Each L1–L4 fitness band has independently configurable distance and elevation multipliers.',
    details: [
      'Level 1 is least active and Level 4 is most active; there is no combined Level 1–2 row and no Level 5 row.',
      'The resolved band from target.difficulty-level selects exactly one of the four rows.',
      'The distance multiplier scales the top-distance-days sum.',
      'The elevation multiplier scales the top-elevation-days sum.',
      'Elevation is the priority. Distance multipliers intentionally do not increase with every fitness band: mileage balances the climbing workload and time on feet.',
      'These multipliers define the full canonical target. Short plans retain only their scheduled weeks of that target.',
      'The table below shows code defaults; the active Plan Algorithm configuration can tune every value independently.',
      'Admin validation requires multipliers to be greater than 0 and at most 20.'
    ],
    table: {
      columns: ['Fitness band', 'Default distance multiplier', 'Default elevation multiplier'],
      rows: [
        ['L1 · Least active', '4.1x', '3.0x'],
        ['L2 · Light activity', '4.8x', '3.5x'],
        ['L3 · Regular activity', '3.5x', '4.3x'],
        ['L4 · Most active', '4.1x', '5.0x']
      ]
    },
    sources: ['backend/Models/TrainingPlans/PlanGenerationDefaults.cs', 'backend/Models/TrainingPlans/PlanGenerationConfig.cs:334-381', 'backend/Services/TrainingPlans/PlanGenerationConfigService.cs:324-343']
  },
  {
    id: 'target.high-altitude-elevation',
    title: 'High-altitude goals enforce at least a 4.0x elevation multiplier.',
    summary: 'The elevation multiplier is raised for goals that reach 13,000 ft or higher.',
    details: [
      'The high-altitude threshold is 13,000 ft (3,962.0 m).',
      'HighAltitudeMinMultiplier is 4.0.',
      'Only the elevation multiplier is raised; distance multiplier remains difficulty-table driven.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanTargetPolicyPlanner.cs:19-20', 'backend/Services/TrainingPlans/TrainingPlanTargetPolicyPlanner.cs:271-285']
  },
  {
    id: 'target.base-phase-half',
    title: 'Pre-final-12 base segment targets are half peak intensity.',
    summary: 'When a goal-hike plan has a PreBase segment, its total targets are derived from half the peak weekly targets.',
    details: [
      'Base phase intensity multiplier is 0.5.',
      'Peak weekly distance and elevation are computed from peak total divided by peak duration weeks.',
      'Base segment total distance/elevation equals half peak weekly load times baseDurationWeeks.',
      'Base segment pack target is half the peak pack target.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanTargetPolicyPlanner.cs:15', 'backend/Services/TrainingPlans/TrainingPlanTargetPolicyPlanner.cs:97-118']
  },
  {
    id: 'target.waterfall',
    title: 'Target waterfall traces load changes through generation.',
    summary: 'Plan tester responses include a diagnostic waterfall from raw target to final generated workouts.',
    details: [
      'The waterfall starts with raw total distance, elevation, and pack target before weekly distribution. For shortened plans, distance and elevation include only the retained canonical weeks; omitted volume is not debt.',
      'Each captured stage stores totals, deltas from the prior stage, debt from the original target, linked rule ids, and per-week snapshots when available.',
      'Stages cover weekly distribution, prorating and phase clamps, pack progression, durability soft caps, TTL debt, weekly ceilings, workout assembly, milestone insertion, protected windows, milestone debt, rounding, structure normalization, and final workouts.',
      'If final workout distance or elevation is more than 10% below the original target, generation emits a warning pointing reviewers to the waterfall.'
    ],
    sources: [
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs:179-552',
      'backend/Services/TrainingPlans/TrainingPlanTargetWaterfallBuilder.cs',
      'backend/Models/TrainingPlans/PlanTargetWaterfall.cs',
      'dashboard/src/pages/TrainingPlanTestPage.tsx:550-624'
    ]
  }
]

export const WEEKLY_GENERATION_RULES: CodifiedPlanRule[] = [
  {
    id: 'week.distribution-curve',
    title: 'The 12-point curve defines canonical weekly volume.',
    summary: 'Weekly distance and elevation use the 12-week average and the corresponding canonical curve factor.',
    details: [
      'The default curve is applied to distance and elevation.',
      'Custom curves are normalized to 12 points first. Short plans then retain the final points without rescaling them.',
      'Each retained week multiplies the full goal distance/elevation divided by 12 by that week factor; configured floors and distance caps still apply.'
    ],
    table: {
      columns: ['Week point', 'Factor'],
      rows: [
        ['1', '0.70'],
        ['2', '0.85'],
        ['3', '0.90'],
        ['4', '0.95'],
        ['5', '1.00'],
        ['6', '1.05'],
        ['7', '1.15'],
        ['8', '1.25'],
        ['9', '1.35'],
        ['10', '1.40'],
        ['11', '0.85'],
        ['12', '0.70']
      ]
    },
    sources: ['backend/Models/TrainingPlans/PlanGenerationConfig.cs', 'backend/Services/TrainingPlans/TrainingPlanWeekTargetBuilder.cs']
  },
  {
    id: 'week.week1-lowest',
    title: 'Canonical week 1 cannot exceed later pre-peak build-up weeks.',
    summary: 'The full canonical curve is corrected before a shorter plan selects its final weeks.',
    details: [
      'The peak index is found from the maximum factor.',
      'The minimum factor between week 2 and the peak is computed.',
      'If week 1 is higher than that minimum build-up factor, week 1 is lowered to it.',
      'Taper weeks after peak are allowed to be lower than week 1. A shortened plan does not reapply this correction to its first retained week.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWeekTargetBuilder.cs:65-90']
  },
  {
    id: 'week.prebase-shape',
    title: 'PreBase week factors are flat and use a reduced pack factor.',
    summary: 'PreBase does not use the normal distribution curve.',
    details: [
      'Every PreBase distance/elevation factor is 0.75.',
      'Every PreBase pack factor from WeekTargetBuilder is 0.40.',
      'Durability pack progression can further cap PreBase pack progression at 0.75.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWeekTargetBuilder.cs:44-45', 'backend/Services/TrainingPlans/TrainingPlanWeekTargetBuilder.cs:92-106', 'backend/Services/TrainingPlans/TrainingPlanDurabilityPlanner.cs:299-302']
  },
  {
    id: 'target.phase-clamp',
    title: 'Weekly target floors follow the hike’s plan-generation profile.',
    summary: 'Standard hikes use the configured distance and elevation floors; DayHike goals keep smaller itinerary-derived targets.',
    details: [
      'Every tour-operator hike and popular hike has a PlanGenerationProfile; existing and newly created hikes default to Standard.',
      'Standard uses the configured weekly distance floor of about 10 miles (16.0 km) and elevation floor of about 984 ft (300.0 m).',
      'DayHike sets those two minimum floors to 0 at both weekly-target creation and the later Base/Peak phase clamp.',
      'A 0 floor does not force the weekly targets to 0; distance and elevation still come from the hike itinerary and normal weekly distribution.',
      'PreBase still drops the phase-clamp floors to 0; its separate age-cohort minimum rule is unchanged.',
      'Weekly distance cannot exceed 30 miles (48.28 km), or the lower configured limit, for either profile; elevation has no weekly ceiling.',
      'Durability limits, blackout handling, milestones, workout assembly, and all other safety rules continue to apply to DayHike plans.',
      'LongItinerary (popular hikes only, for thru-hikes) builds plan targets from the itemized itinerary days, the same as Standard, and keeps the Standard floors. The hike also stores a whole-trip distance and elevation gain.',
      'In the app, trip days after the itemized ones show the remaining trip distance and gain averaged per day. Modify is hidden on mobile for LongItinerary hikes in v1.'
    ],
    table: {
      columns: ['Target', 'Standard minimum', 'DayHike minimum', 'Maximum'],
      rows: [
        ['Distance', '10 miles (16.0 km), except PreBase phase min 0', '0', '30 miles (48.28 km)'],
        ['Elevation', '984 ft (300.0 m), except PreBase phase min 0', '0', 'No cap']
      ]
    },
    sources: [
      'HikeStrong.Db/Models/PlanGenerationProfile.cs',
      'backend/Services/TrainingPlans/GoalHikeResolver.cs',
      'backend/Services/TrainingPlans/TrainingPlanWeekTargetBuilder.cs',
      'backend/Services/TrainingPlans/TrainingPlanTargetPolicyPlanner.cs',
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs',
      'Codified rule id: target.phase-clamp'
    ]
  },
  {
    id: 'blackout.redistribution',
    title: 'Fully blacked-out weeks redistribute segment factors to remaining weeks.',
    summary: 'Blackout weeks do not simply delete volume unless the whole segment is blacked out.',
    details: [
      'Fully blacked-out week indices get factor 0.',
      'Remaining non-blacked factor values are scaled by totalBefore / remainingSum.',
      'The redistribution returns false if every distance factor is zero after blackout handling.',
      'Distance and elevation factors are redistributed separately but with the same fully blacked week set.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWeekTargetBuilder.cs:227-256']
  },
  {
    id: 'blackout.full-week',
    title: 'A fully blacked-out week becomes a forced rest week.',
    summary: 'When every day in a week is blocked, the week has zero targets and no workouts.',
    details: [
      'Target distance, elevation, and average pack are set to 0.',
      'IntensityFactor is set to 0.',
      'The week receives the BlackoutWeek flag.',
      'Durability cap state carries across the break so the return week ramps from pre-break load rather than from zero.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanGenerator.cs:572-597']
  },
  {
    id: 'blackout.partial-week',
    title: 'Partially blacked-out weeks prorate targets and train only on open days.',
    summary: 'A partial blackout reduces the effective weekly target by trainable days.',
    details: [
      'daysRatio equals available days in this generated week divided by 7.',
      'trainableRatio equals non-blacked available days divided by available days.',
      'Distance and elevation targets are phase-clamped, then multiplied by daysRatio and trainableRatio.',
      'Requested workout count is capped to trainable days and at least one workout when any trainable day remains.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanGenerator.cs:599-612', 'backend/Services/TrainingPlans/TrainingPlanGenerator.cs:747-752']
  },
  {
    id: 'durability.cap.elevation',
    title: 'Goal-hike elevation growth is observed as a soft cap.',
    summary: 'For goal-hike plans, standalone elevation comfort excess is traced but not destructively removed before optional TTL.',
    details: [
      'Default elevation max increase fraction is 0.20.',
      'The cap is previous elevation multiplied by 1 + ElevationMaxIncreaseFraction.',
      'For goal-hike plans this is diagnostic-only; optional TTL can handle progression control when enabled.',
      'General-fitness plans may still apply the standalone cap destructively.',
      'If previous elevation was zero, the elevation-specific cap does not fire.'
    ],
    sources: ['backend/Models/TrainingPlans/PlanGenerationConfig.cs', 'backend/Services/TrainingPlans/TrainingPlanDurabilityPlanner.cs']
  },
  {
    id: 'durability.cap.pack',
    title: 'Pack growth is capped week over week.',
    summary: 'If the prior week had pack load, the next week cannot exceed the configured pack step cap.',
    details: [
      'Default pack step max increase fraction is 0.10.',
      'The cap is previous pack weight multiplied by 1 + PackStepMaxIncreaseFraction.',
      'If previous pack was zero, the pack-specific cap does not fire.'
    ],
    sources: ['backend/Models/TrainingPlans/PlanGenerationConfig.cs:251-252', 'backend/Services/TrainingPlans/TrainingPlanDurabilityPlanner.cs:150-158']
  },
  {
    id: 'durability.cap.ttl',
    title: 'Total training load growth can be capped week over week.',
    summary: 'TTL is an optional guardrail. When enabled, it combines normalized distance, elevation, and pack, caps cohort-specific growth, then turns removed distance/elevation into repayable debt.',
    details: [
      'TTL is disabled by default for generation runs and must be explicitly opted into.',
      'Default TTL weights are 0.45 distance, 0.35 elevation, 0.20 pack.',
      'Default TTL max increases are 25% for under-40/no-joint, 20% for under-40/joint, 20% for 40+/no-joint, and 15% for 40+/joint.',
      'Joint-sensitive cohorts include the onboarding joints concern and lower-body joint injury history.',
      'If TTL exceeds the cap, distance and elevation are scaled down first while preserving pack when possible.',
      'Removed distance/elevation is recorded as durability debt for the repayment pass instead of silently disappearing when TTL is enabled.'
    ],
    table: {
      columns: ['TTL setting', 'Default'],
      rows: [
        ['Distance weight', '0.45'],
        ['Elevation weight', '0.35'],
        ['Pack weight', '0.20'],
        ['Under 40, no joint concern', '25%'],
        ['Under 40, joint concern', '20%'],
        ['Age 40+, no joint concern', '20%'],
        ['Age 40+, joint concern', '15%']
      ]
    },
    sources: ['backend/Models/TrainingPlans/PlanGenerationConfig.cs', 'backend/Services/TrainingPlans/TrainingPlanDurabilityPlanner.cs', 'backend/Services/TrainingPlans/TrainingPlanDurabilityDebtPlanner.cs']
  },
  {
    id: 'durability.debt-repaid',
    title: 'TTL debt is repaid into eligible weeks.',
    summary: 'When optional TTL is enabled, distance/elevation debt from TTL caps is repaid into non-protected, non-milestone workouts when capacity is available.',
    details: [
      'This rule is skipped when the optional TTL cap is disabled.',
      'Repayment prefers the source week, then future non-milestone weeks, then previous non-milestone weeks.',
      'Milestone weeks are a fallback only when no cleaner eligible week can absorb the debt.',
      'Elevation repayment skips protected milestone-window dates and applies spike guards against the previous non-taper week and a pre-TTL target-week baseline.',
      'Elevation capacity is ordinary elevation cap minus current workout elevation, so a 100 ft host under the 5,000 ft default cap has about 4,900 ft available.',
      'Safe distance workouts can be promoted to mixed elevation hosts when the user has true or artificial elevation access.',
      'Unpaid traces include aggregate candidate rejection reasons such as protected window, milestone day, ordinary cap, spike guard, or missing elevation-capable primary.',
      'Unpaid TTL debt is reported in generation traces and target-waterfall notes.'
    ],
    table: {
      columns: ['Spike guard', 'Default', 'Protective'],
      rows: [
        ['Previous non-taper week elevation', '50%', '35%'],
        ['Original target week elevation', '25%', '20%']
      ]
    },
    sources: ['backend/Services/TrainingPlans/TrainingPlanDurabilityDebtPlanner.cs', 'backend/Models/TrainingPlans/PlanGenerationConfig.cs']
  },
  {
    id: 'durability.debt-created',
    title: 'TTL cap reductions become durability debt.',
    summary: 'When optional TTL caps a week, removed distance/elevation is recorded explicitly before repayment.',
    details: [
      'This rule is skipped when the optional TTL cap is disabled.',
      'Debt source traces retain the source week and durability.cap.ttl rule id.',
      'Pack overage can be measured for diagnostics, but the repayment pass currently targets distance and elevation.',
      'Target-waterfall notes summarize total TTL debt created.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingMetricDebt.cs', 'backend/Services/TrainingPlans/TrainingPlanDurabilityDebtPlanner.cs']
  },
  {
    id: 'durability.debt-unpaid',
    title: 'Unpaid TTL debt is reported.',
    summary: 'When optional TTL is enabled, TTL debt that cannot fit within capability, structure, protected-window, weekly-ceiling, and spike constraints stays visible.',
    details: [
      'This rule is skipped when the optional TTL cap is disabled.',
      'Unpaid debt creates warnings when it exceeds 10% of the original distance or elevation target.',
      'Post-rounding or weekly-ceiling losses are reconciled against final workouts.',
      'The target waterfall keeps explicit unpaid-debt notes for admin/tester export.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingMetricDebt.cs', 'backend/Services/TrainingPlans/TrainingPlanGenerator.cs']
  },
  {
    id: 'pack.progression',
    title: 'Pack weight ramps toward the target over time.',
    summary: 'Weekly pack prescription is a ramped fraction of the plan pack target.',
    details: [
      'If target pack is 0 or less, pack progression returns 0.',
      'Users under 40 reach full pack by week 7.',
      'Users age 40 or older reach full pack by week 9.',
      'Pack-sensitive injuries push the full-pack week one week later when the full-pack week is below 9.',
      'Before full-pack week, progression is canonical weekNumber / fullPackWeek clamped between 0.15 and 1.0. Short plans retain the corresponding 12-week pack progression.',
      'PreBase caps progression at 0.75.',
      'Taper never raises pack above the week base pack factor.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanDurabilityPlanner.cs:275-313']
  },
  {
    id: 'cohort.minimum',
    title: 'PreBase weeks have age-cohort minimum volumes.',
    summary: 'For goal-hike PreBase weeks, cohort minimums can raise distance/elevation after durability guardrails.',
    details: [
      'This rule only applies in PreBase for goal-hike plans.',
      'Age threshold is 40 years.',
      'Under-40 minimum is about 9 miles and 1,900 ft per full week (14.5 km and 579 m).',
      'Age 40+ or unknown-age minimum is about 7 miles and 1,400 ft per full week (11.3 km and 427 m).',
      'Minimums are multiplied by daysRatio for partial weeks.',
      'Elevation is only floored when the week already has elevation; users with no elevation access keep zero elevation.',
      'This rule supersedes the distribution curve and durability guardrails.'
    ],
    table: {
      columns: ['Cohort', 'Min distance', 'Min elevation'],
      rows: [
        ['Under 40', '9 miles (14.5 km)', '1,900 ft (579 m)'],
        ['40+ or unknown', '7 miles (11.3 km)', '1,400 ft (427 m)']
      ]
    },
    sources: ['backend/Models/TrainingPlans/PlanGenerationConfig.cs:255-280', 'backend/Services/TrainingPlans/TrainingPlanGenerator.cs:684-715']
  },
  {
    id: 'weekly.ceiling.pre-round',
    title: 'Hard weekly distance ceiling clamps generated weekly targets before assembly.',
    summary: 'After cohort minimums, weekly distance is clamped to 30 miles (48.28 km) or the lower configured ceiling; elevation is not capped.',
    details: [
      'Distance is capped at config.WeeklyMaxDistanceKm multiplied by daysRatio.',
      'Elevation keeps the generated target and is not capped by config.WeeklyMaxElevationGainMeters.',
      'The historical 48.28 km setting resolves to 30 full miles using the same conversion as assignment rounding; lower configured caps remain lower.',
      'This trace fires before workout assembly when the pre-assembly week target was lowered.'
    ],
    sources: ['backend/Models/TrainingPlans/PlanGenerationConfig.cs:43-59', 'backend/Services/TrainingPlans/TrainingPlanGenerator.cs:717-733']
  },
  {
    id: 'workout.count.phase-ttl',
    title: 'Training-day count varies by phase and TTL.',
    summary: 'The requested training days are treated as an upper preference inside phase and total training-day constraints.',
    details: [
      'Requested training days are clamped 1-7.',
      'Max training days is the minimum of available days and MaxTotalTrainingDays.',
      'Full weeks use minTrainingDays = min(maxTrainingDays, min(3, requestedTrainingDays)).',
      'Partial weeks use minTrainingDays = min(maxTrainingDays, max(1, availableDays / 2)).',
      'Phase preference: PreBase/Taper minimum, Base minimum + 1, Build max - 1 or minimum + 1, Peak maximum.',
      'If requested days are at least 5 and WeekTtlScore is at least 1.15, the week uses maxTrainingDays.',
      'WeekTtlScore >= 1.05 nudges up; WeekTtlScore <= 0.95 nudges down.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs:52-57', 'backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs:908-962']
  },
  {
    id: 'workout.date-selection',
    title: 'Workout dates preserve the resolved count while scoring preference, spacing, and variety.',
    summary: 'The generator keeps the phase/TTL training-day count and scores candidate date combinations rather than picking days greedily.',
    details: [
      'Preferred days are used when enough preferred dates are available; otherwise all available days are candidate dates.',
      'Date selection never subtracts a training day merely to avoid repeating the prior week or to create omitted-weekday variety.',
      'Candidate score rewards preferred-day count, wide span, and minimum gap.',
      'Candidate score penalizes adjacent pairs, max consecutive dates, repeated prior weekday counts, overlap with the previous active week, and exact previous-week repetition.',
      'When the phase/TTL count already requires an omission, omitted-weekday score favors a Monday-first rotating weekday, weekdays not omitted in the recent four-week window, and weekdays that were used often or used in the previous week.',
      'Repeated omitted weekdays are penalized so the same weekday does not stay excluded from training work week after week.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs']
  },
  {
    id: 'workout.long-elevation-roles',
    title: 'Each normal week chooses long and elevation training roles.',
    summary: 'A non-partial week, or partial week with more than two training days, receives a long day and an elevation day.',
    details: [
      'Partial weeks with two or fewer training days skip the long/elevation role distinction.',
      'If there is only one training date, the elevation date is the long date.',
      'Long date rotates away from prior long weekdays where possible.',
      'Elevation date is chosen as far from the long day as possible.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs:92-103', 'backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs:1124-1172']
  },
  {
    id: 'workout.load-split',
    title: 'Weekly distance and elevation are split by phase-specific fractions.',
    summary: 'Long and elevation sessions take configured portions before secondary days divide the remainder.',
    details: [
      'Base long day takes 38% of weekly distance and 30% of weekly elevation.',
      'Build and Peak long days take 45% of weekly distance and 45% of weekly elevation.',
      'Default long day takes 40% of weekly distance and 35% of weekly elevation.',
      'Base elevation session takes 45% of weekly elevation.',
      'Build and Peak elevation sessions take 40% of weekly elevation.',
      'Default elevation session takes 35% of weekly elevation.',
      'The dedicated elevation day also shares remaining distance so every hike day carries both distance and elevation.',
      'Remaining distance/elevation are split across secondary/elevation-share days.'
    ],
    table: {
      columns: ['Phase', 'Long distance', 'Long elevation', 'Elevation-session elevation'],
      rows: [
        ['Base', '38%', '30%', '45%'],
        ['Build', '45%', '45%', '40%'],
        ['Peak', '45%', '45%', '40%'],
        ['Default', '40%', '35%', '35%']
      ]
    },
    sources: ['backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs:104-163']
  },
  {
    id: 'workout.grade-cap',
    title: 'Single-workout steepness is capped per fitness band.',
    summary: 'No workout may prescribe more elevation per distance than the fitness-band grade cap; excess spreads across the week, then adds distance, then trims as a last resort.',
    details: [
      'The cap is configured as max elevation meters per km for each difficulty level (0 disables a band).',
      'During assembly, a too-steep day first spreads excess elevation to other elevation days in the week with grade headroom.',
      'Remaining excess is satisfied by adding distance to the day, up to the configured hard weekly distance ceiling.',
      'Only when the weekly distance ceiling leaves no room is elevation trimmed, with a workout.grade-cap trace entry.',
      'Elevation milestones are never elevation-reduced by the grade cap; they carry enough distance to keep the climb within the cap instead.',
      'Milestone split components carry their own grade-cap distance; components whose exercise does not contribute distance (stair climb, stair machine, step-ups) carry none.'
    ],
    table: {
      columns: ['Fitness level', 'Max grade'],
      rows: [
        ['Level 1', '105 m/km (~555 ft/mi)'],
        ['Level 2', '130 m/km (~685 ft/mi)'],
        ['Level 3', '160 m/km (~845 ft/mi)'],
        ['Level 4', '190 m/km (~1,005 ft/mi)']
      ]
    },
    sources: [
      'backend/Models/TrainingPlans/PlanGenerationConfig.cs:415-437',
      'backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs:368-528',
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:250-268',
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs:1366'
    ]
  },
  {
    id: 'treadmill.incline-cap',
    title: 'Treadmill workouts cannot climb beyond the max incline.',
    summary: 'When the treadmill is the only elevation modality, workout elevation is capped at treadmill distance times the configured max incline (default 12%).',
    details: [
      'Default max incline is 12%, which allows about 634 ft per mile (120 m per km); the setting is configurable 0-50%.',
      'During assembly the cap applies to artificial-elevation days where the treadmill is the only usable modality: treadmill with incline access, no stair machine, and knee issues or no step-up access.',
      'Assembly first adds treadmill distance (up to the treadmill distance cap) to make the elevation achievable, then trims the remainder with a treadmill.incline-cap trace entry.',
      'A final safety net in exercise target sync caps any treadmill exercise at its distance times the max incline, moving excess elevation to another elevation-capable exercise in the workout when one exists.',
      'For newly generated plans with TTL disabled, final sync first uses spare weekly distance capacity to preserve assigned climbing on the same treadmill session. The existing treadmill distance limit and milestone exception still apply.',
      'When the week has insufficient room, eligible ordinary distance-only sessions can release whole-unit mileage while retaining at least one unit. Milestones, completed/imported workouts, climbing sessions, and protected dates are excluded as donors.',
      'This recovery does not add elevation, use protected recovery dates, move work between weeks, or apply to completion-time adjustments.',
      'When no other exercise can carry the remaining excess, the workout target is trimmed and rounded down to the user display increment so rounding cannot exceed the incline limit.',
      'Treadmill-hosted milestone components use the lower of the fitness-band grade cap and the incline-implied grade when computing carry distance.'
    ],
    sources: [
      'backend/Models/TrainingPlans/PlanGenerationConfig.cs:163',
      'backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs:368-528',
      'backend/Services/TrainingPlans/TrainingPlanExerciseTargetSynchronizer.cs',
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:918-940'
    ]
  },
  {
    id: 'workout.true-elevation-count',
    title: 'A minimum fraction of elevation workouts must use true elevation when available.',
    summary: 'The generator selects true-elevation dates initially, then restores the configured minimum after milestone and cleanup mutations.',
    details: [
      'Default true-elevation minimum fraction is 0.50.',
      'Joint/knee concerns use 0.30.',
      'Initial assembly uses ceiling(trainingDates.Count * fraction), capped to trainingDates.Count.',
      'Final reconciliation counts non-milestone primary workouts that still have elevation after milestone insertion, remediation, rounding, and concentration cleanup.',
      'When the user has true-elevation access and no artificial elevation access, all final elevation hosts use true elevation.',
      'If the final true-elevation count is short, eligible elevation-role hosts are promoted first, then lower-load hosts, while preserving weekly workout targets and support exercises.',
      'If the user has no true-elevation access, the count is 0.'
    ],
    sources: ['backend/Models/TrainingPlans/PlanGenerationConfig.cs', 'backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs', 'backend/Services/TrainingPlans/TrainingPlanElevationModalityReconciler.cs']
  },
  {
    id: 'workout.pack-count',
    title: 'A fraction of training days carry pack when weekly pack weight is positive.',
    summary: 'Pack dates are selected separately from true-elevation dates and prefer the long day first.',
    details: [
      'Default pack workout fraction is 0.70.',
      'Joint/knee concerns use 0.50 and cap pack fraction at 0.50.',
      'Other pack-sensitive injuries cap pack fraction at 0.60.',
      'If WeekPackWeightKg is 0 or less, pack count is 0.',
      'Otherwise pack count is ceiling(trainingDates.Count * PackFraction), capped to trainingDates.Count.'
    ],
    sources: ['backend/Models/TrainingPlans/PlanGenerationConfig.cs:154-164', 'backend/Services/TrainingPlans/TrainingPlanGenerator.cs:523-533', 'backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs:175-194']
  },
  {
    id: 'workout.hiit-eligibility',
    title: 'HIIT appears only under strict eligibility rules.',
    summary: 'HIIT is high-altitude-specific and injury/readiness-gated.',
    details: [
      'Taper weeks never allow HIIT.',
      'Goal max altitude must be at least 8,000 ft (2,438 m).',
      'Week number must be at least 4.',
      'Achilles or lower-back injury history blocks HIIT.',
      'Endurance-frequency answers do not block HIIT; safety and recovery gates decide eligibility.',
      'Two or more TooHard completions in the previous seven days suppress HIIT.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanTargetPolicyPlanner.cs:164-200']
  },
  {
    id: 'workout.hiit-placement',
    title: 'HIIT avoids long/elevation stress days but may sit next to long distance.',
    summary: 'When HIIT is allowed, it is placed on a training date away from the dedicated elevation role, while long-distance adjacency is permitted.',
    details: [
      'HIIT is included only when HiitDurationMinutes is positive, week >= 4, and not Taper.',
      'Candidate HIIT dates exclude the long-distance date and the dedicated elevation date.',
      'Candidate HIIT dates may be one calendar day away from the long-distance date.',
      'Candidate HIIT dates must be more than one calendar day away from the dedicated elevation date.',
      'Protected elevation-milestone windows later strip HIIT from non-milestone workouts inside those dates.',
      'If no candidate satisfies the spacing rule, no HIIT date is selected.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs:180-194', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:402-425']
  },
  {
    id: 'exercise.true-elevation-selection',
    title: 'True-elevation exercise choice depends on distance, elevation, access, and knee concerns.',
    summary: 'The prescription builder chooses true-elevation primaries from an ordered access-aware set.',
    details: [
      'Long distance is at least about 5 miles (8.0 km).',
      'Non-milestone true-elevation workouts with distance and zero elevation are treated as distance-only and prefer RUN before HIKE.',
      'Long-distance true-elevation workouts prefer HIKE when local trails are available, then HILL_REPEATS, STAIR_CLIMB, STEP_UPS based on access.',
      'Shorter elevation workouts prefer HIKE only when elevation is zero; otherwise they prefer hill repeats, stairs, step-ups, and local hikes according to access.',
      'Long hikes with local trail access choose outdoor distance target codes first.',
      'Hill repeats are avoided when elevation target is zero if another true-elevation exercise is available.',
      'If elevation exceeds about 1,640 ft (500 m) and the user has no knee issues, an additional hill/stair repeat component can be added.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs:42-127']
  },
  {
    id: 'exercise.artificial-elevation-selection',
    title: 'Artificial-elevation exercise choice changes for knee concerns.',
    summary: 'Initial preference changes for knee concerns; final reconciliation alternates the accessible artificial equipment across the week.',
    details: [
      'With knee issues, artificial elevation prefers TREADMILL before STAIR_MACHINE.',
      'Without knee issues, artificial elevation prefers STAIR_MACHINE before TREADMILL.',
      'When both are accessible, final same-week artificial elevation hosts alternate TREADMILL and STAIR_MACHINE instead of selecting each host independently from a date hash.',
      'Joint/knee users start the final alternation with TREADMILL; other users start with STAIR_MACHINE.',
      'When only one artificial option is accessible, every artificial host uses that available option.',
      'Fallback selection requires ExerciseCategory.Elevation, IsArtificialElevation true, and knee-friendly matching when requested.',
      'If no artificial primary is available, the workout can end up without an artificial-elevation primary.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs', 'backend/Services/TrainingPlans/TrainingPlanElevationModalityReconciler.cs']
  },
  {
    id: 'exercise.elevation-modality.final',
    title: 'Final cleanup reconciles elevation modality and artificial-equipment diversity.',
    summary: 'After plan mutations settle, elevation hosts are reconciled to the configured true-elevation minimum and accessible artificial equipment is rotated.',
    details: [
      'The rule runs after milestone insertion, structure remediation, assignment rounding, weekly ceilings, and final semantic cleanup.',
      'It preserves existing true-elevation hosts and promotes only enough eligible artificial hosts to restore the configured minimum.',
      'It replaces only elevation load primaries; Balance, Lunge, and HIIT support exercises remain attached.',
      'Workout distance, elevation, pack, and duration targets are preserved while the primary exercise is rebuilt.',
      'A paired before/after trace records each workout whose final elevation modality changed.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanElevationModalityReconciler.cs', 'backend/Services/TrainingPlans/TrainingPlanGenerator.cs']
  },
  {
    id: 'exercise.treadmill-cap',
    title: 'Single treadmill workouts have a distance cap and overflow workout.',
    summary: 'Treadmill distance above the cap is moved to an added run/hike exercise.',
    details: [
      'Default treadmill cap is about 10 miles (16.093 km).',
      'The cap applies only when the artificial-elevation primary is TREADMILL and primaryDistanceKm exceeds the cap.',
      'Overflow distance is primaryDistanceKm minus treadmill cap.',
      'If overflow is at least about 0.06 miles (0.1 km), an added RUN/HIKE exercise covers the overflow distance.',
      'Milestone workouts are exempt: the treadmill keeps the full milestone distance so the milestone stays a single-exercise workout.'
    ],
    sources: ['backend/Models/TrainingPlans/PlanGenerationConfig.cs:125-130', 'backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs:153-181']
  },
  {
    id: 'exercise.stepups-threshold',
    title: 'Step-ups can share an artificial-elevation target for non-knee users.',
    summary: 'The elevation-tracking STEP_UPS workout is added only when elevation is high enough and the user has step-up access.',
    details: [
      'The step-up supplemental threshold is elevation above about 656 ft (200 m).',
      'The user must not have knee issues.',
      'The user must have TrueElevationWorkoutAccess.StepUps.',
      'The supplemental component uses the canonical STEP_UPS exercise from the active catalog.',
      'Final assignment rounding gives Step Ups its share of the elevation target and the independent-card split preserves that elevation and any assigned pack weight.',
      'Supplemental Step Ups do not use strength-style sets or reps.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs:197-205', 'backend/Services/TrainingPlans/TrainingPlanAssignmentRounding.cs:464-515', 'backend/Services/TrainingPlans/TrainingPlanGeneratedWorkoutSplitter.cs:30-68']
  },
  {
    id: 'exercise.distance-only-selection',
    title: 'Distance-only training days pick run before hike.',
    summary: 'A training day with distance but no elevation component still gets a concrete activity.',
    details: [
      'Non-milestone distance-only days first prefer RUN, even when they started from true-elevation selection or a protected milestone-window restriction.',
      'If RUN is unavailable, they prefer HIKE.',
      'Distance milestones remain on the milestone distance-prescription path and are not changed by this fallback.',
      'The selected primary carries the full day distance target.'
    ],
    sources: [
      'backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs:42-52',
      'backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs:196-207',
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:588-639'
    ]
  },
  {
    id: 'exercise.hiit-standard',
    title: 'HIIT prescriptions use the generic HIIT_STANDARD when available.',
    summary: 'The backend does not pick a specific HIIT modality for generated plans when HIIT_STANDARD exists.',
    details: [
      'SelectHiitExercise first returns HIIT_STANDARD.',
      'If HIIT_STANDARD is unavailable, it falls back to any active HIIT category exercise.',
      'The code comment states runtime selects the specific equipment/format.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs:355-364']
  },
  {
    id: 'rounding.distance',
    title: 'Distance assignments round up by the user measurement unit.',
    summary: 'Small positive distance targets are never left as tiny decimal assignments.',
    details: [
      'Imperial users get at least 1 mile and distances round up to whole miles, then normalize back to backend storage units.',
      'Metric users get at least 1 km and distances round up to whole kilometers.',
      'Exercise-level distance targets are rounded the same way as workout-level distance targets.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanAssignmentRounding.cs:13-14', 'backend/Services/TrainingPlans/TrainingPlanAssignmentRounding.cs:64-70', 'backend/Services/TrainingPlans/TrainingPlanAssignmentRounding.cs:296-317']
  },
  {
    id: 'rounding.elevation',
    title: 'Elevation assignments round up before concentration cleanup.',
    summary: 'Elevation is rounded to clean display increments while preserving fractional meters in exercise targets; ordinary-workout concentration is handled by a later semantic pass.',
    details: [
      'Imperial users get at least 100 ft and elevation rounds up to 5 ft increments, then normalize back to backend storage units.',
      'Metric users get at least 30 m and elevation rounds up to 5 m increments.',
      'Exercise target distribution preserves the fractional-meter total and final remainder, so a 500 ft assignment remains 152.4 m instead of falling to 152 m (499 ft).',
      'Imperial increment rounding uses the exact 0.3048 meters-per-foot conversion so 500 ft does not spuriously advance to 505 ft.',
      'Non-milestone workout caps are enforced after rounding by the elevation concentration cleanup rule.',
      'Generated plans repeat concentration cleanup after final support repair, rounding, and weekly ceiling enforcement so unit rounding cannot push ordinary workouts above the cap.',
      'Milestone workouts keep the same uncapped rounding behavior so configured milestone splits can exceed ordinary-workout caps.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanAssignmentRounding.cs']
  },
  {
    id: 'rounding.pack',
    title: 'Pack assignments round to practical increments.',
    summary: 'Generated pack weights are rounded by measurement unit before display/storage.',
    details: [
      'Imperial users get at least 5 lb and pack rounds to 5 lb increments, then normalize back to backend storage units.',
      'Metric users get at least 2.5 kg and pack rounds to 2.5 kg increments.',
      'Zero or negative pack remains 0.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanAssignmentRounding.cs:21-24', 'backend/Services/TrainingPlans/TrainingPlanAssignmentRounding.cs:78-79', 'backend/Services/TrainingPlans/TrainingPlanAssignmentRounding.cs:353-375']
  },
  {
    id: 'rounding.hill-repeat',
    title: 'Hill-repeat workouts carry elevation and no distance.',
    summary: 'Hill repeats are normalized so distance does not leak into the assignment.',
    details: [
      'If a workout primary is hill repeats, workout.DistanceKm is set to 0.',
      'If the hill-repeat workout has no elevation, it receives the minimum elevation for the user unit.',
      'Hill-repeat exercise-level distance is also set to 0.',
      'Removed hill-repeat distance is not mileage debt: no additional distance is assigned to other sessions. The trace records the elevation-only normalization.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanAssignmentRounding.cs']
  },
  {
    id: 'exercise.semantic.zero-elevation-primary',
    title: 'Elevation-specific primaries need positive elevation.',
    summary: 'The final semantic pass removes stairs, step-ups, hill repeats, stair machine, and incline treadmill when final elevation is zero.',
    details: [
      'If the workout still has distance, the day becomes a distance-only training workout.',
      'If only support work remains, the day becomes Support.',
      'If no work remains, the day becomes Rest.',
      'The cleanup runs after rounding, structure remediation, and support reapplication.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanExerciseSemanticSanitizer.cs:84-181']
  },
  {
    id: 'elevation.concentration.cap',
    title: 'Ordinary elevation workouts have a configurable cap.',
    summary: 'Non-milestone primary training workouts are capped after final rounding to prevent single-session elevation spikes.',
    details: [
      'Default ordinary cap is 5,000 ft (1,524 m).',
      'Protective-bias users use a separately configurable cap, also defaulting to 5,000 ft (1,524 m).',
      'Protective bias is age over 40, joint concern, or lower-body injury history.',
      'Milestone workouts are exempt from this ordinary-workout cap.'
    ],
    sources: ['backend/Models/TrainingPlans/PlanGenerationDefaults.cs', 'backend/Models/TrainingPlans/PlanGenerationConfig.cs:129-146', 'backend/Services/TrainingPlans/TrainingPlanExerciseSemanticSanitizer.cs:190-238']
  },
  {
    id: 'elevation.concentration.redistribute',
    title: 'Capped elevation can move to safe same-week hosts.',
    summary: 'Excess from an overloaded ordinary workout is redistributed only when another same-week non-milestone host has capacity.',
    details: [
      'Candidates must be primary training workouts.',
      'Candidates cannot be milestones and cannot already be at the ordinary cap.',
      'When a zero-elevation elevation-category host receives redistributed gain, it becomes a true-elevation workout.',
      'Exercise-level elevation targets are synced after redistribution.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanExerciseSemanticSanitizer.cs:284-343']
  },
  {
    id: 'elevation.concentration.debt-created',
    title: 'Unsafe concentration overflow becomes explicit debt.',
    summary: 'When same-week capacity is missing, the generator records unassigned elevation rather than dumping it into one host.',
    details: [
      'Debt is created only after the source workout is capped.',
      'The warning points reviewers to target waterfall and concentration traces.',
      'No cross-week repayment is attempted when it would violate ordinary-workout concentration limits.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanExerciseSemanticSanitizer.cs:240-257']
  },
  {
    id: 'elevation.concentration.debt-unpaid',
    title: 'Unpaid concentration debt stays visible.',
    summary: 'The final trace reports concentration excess that could not be safely assigned.',
    details: [
      'The plan keeps capped workouts instead of preserving the unsafe total at any cost.',
      'The trace records the amount left unassigned for audit/debugging.',
      'This is separate from milestone metric debt, which has its own repayment rules.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanExerciseSemanticSanitizer.cs:258-270']
  },
  {
    id: 'exercise.target-sync.final',
    title: 'Final workout targets are synchronized to exercises.',
    summary: 'After generation repairs and post-completion automatic adjustments, exercise targets match workout totals, sharing elevation across selected climbing exercises.',
    details: [
      'Completed or imported workout records are skipped.',
      'Survey throttles, HikeStrong Index adaptations, and balance-progression updates run this synchronization before the adjusted plan is persisted.',
      'Differences that change the stored workout target are corrected rather than being hidden inside the prior broad tolerance.',
      'Elevation comparison and synchronization preserve fractional meters, including sub-meter mismatches that would otherwise turn 500 ft / 50 stair-machine floors into 499 ft / 49.9 floors.',
      'When final elevation targets need repair, dedicated elevation exercises share unassigned elevation equally; existing positive proportions are retained. Shares round to 5 ft or 5 m, with the exact remainder on the last exercise so totals do not increase.',
      'Treadmill distance remains capped and overflow is represented by a hike/run distance carrier, even when the pre-sync exercise distances already add up to the workout total.',
      'Milestone workouts waive the treadmill distance cap so they keep exactly one exercise instead of gaining an overflow carrier.',
      'Step-up, stair, and hill workouts with workout distance get a hike/run distance carrier so distance is not orphaned.',
      'A treadmill incline-cap safety net runs here: generation with TTL disabled uses spare weekly distance capacity first, then eligible same-week ordinary distance-only mileage to preserve assigned climbing. Remaining excess climb moves to another exercise or is trimmed and rounded down within the incline limit.',
      'After final safety cleanup, milestone and durability repayment ledgers are checked again. Lost repayments become unpaid debt in the final comparison and waterfall notes.',
      'The trace reports corrected distance/elevation mismatch counts and any remaining mismatches.'
    ],
    sources: [
      'backend/Services/TrainingPlans/TrainingPlanExerciseTargetSynchronizer.cs',
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs',
      'backend/Services/TrainingPlans/TrainingPlanWorkoutCompletionService.cs'
    ]
  },
  {
    id: 'workout.independent-exercise-split',
    title: 'Generated workout cards are independent workouts.',
    summary: 'After final target sync, generated multi-card workout bundles are split into same-date workouts before original prescriptions are seeded.',
    details: [
      'The split mirrors planner card grouping: lunge and balance progressions remain grouped, while distance, elevation, and HIIT cards become separate workouts.',
      'Each split workout owns only the distance, elevation, duration, and pack values from its exercise group.',
      'Weekly prescribed distance/elevation is recomputed from the split workouts, so card totals and weekly totals match at plan creation.',
      'The first primary card keeps long-workout metadata; support groups become support workouts with zero distance and elevation.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanGeneratedWorkoutSplitter.cs', 'backend/Services/TrainingPlans/TrainingPlanGenerator.cs']
  },
  {
    id: 'weekly.ceiling.post-round.distance',
    title: 'Post-rounding weekly distance cannot exceed the hard ceiling.',
    summary: 'Generation and later automatic changes enforce 30 miles (48.28 km), or the lower configured distance cap.',
    details: [
      'All prescribed workout distance counts toward the ceiling; recorded imports are excluded.',
      'Completed and past prescriptions reserve capacity. Only eligible pending prescriptions are reduced to fit the remaining capacity; historical actuals are never rewritten.',
      'Remaining capacity is allocated in whole user units. Elevation-bearing sessions take priority before distance-only milestones and ordinary distance; allocations never increase the original prescriptions.',
      'Proportional shares and largest fractional remainders use available whole units without growing any original prescription or exceeding the ceiling.',
      'Exercise-level distance targets are synced after allocation.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanAssignmentRounding.cs']
  }
]

export const STRUCTURE_SUPPORT_RULES: CodifiedPlanRule[] = [
  {
    id: 'structure.normalize-week',
    title: 'Every generated week is normalized to one prescribed day per date.',
    summary: 'The week structure planner creates a complete calendar week shape before enforcing caps.',
    details: [
      'WeekStartDate and WeekEndDate are normalized to dates.',
      'A generated week is clamped to at most 7 calendar days.',
      'Workouts outside the week range are ignored.',
      'If multiple workouts exist on the same date, milestone workouts are preserved; otherwise the highest-stress workout wins.',
      'Missing dates are filled with explicit rest days.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWeekStructurePlanner.cs:113-177']
  },
  {
    id: 'structure.classify-day',
    title: 'Every day is classified as Training, Support, or Rest.',
    summary: 'Classification is derived from training metrics and exercise categories, then used by structure caps.',
    details: [
      'A day is Training when it has distance, elevation, pack, HIIT, elevation exercise, or distance exercise.',
      'A day with exercises but no primary training work is Support.',
      'Support days have distance, elevation, and pack zeroed.',
      'Rest days have no exercises, zero load, rest session type, and rest completion criteria.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWeekStructurePlanner.cs:215-336']
  },
  {
    id: 'structure.cap.table',
    title: 'Phase structure caps define training and rest limits.',
    summary: 'These are the v3.4 structure rules referenced by remediation warnings.',
    details: [
      'Training days are any non-rest day, including primary training and Support days.',
      'Rest days are explicit zero-load rest days.',
      'There is no separate primary-training-day cap and no consecutive-training-day cap.'
    ],
    table: {
      columns: ['Phase', 'Max training days', 'Min rest days'],
      rows: [
        ['PreBase', '4', '3'],
        ['Base', '5', '2'],
        ['Build', '6', '1'],
        ['Peak', '6', '1'],
        ['Taper', '4', '3']
      ]
    },
    sources: ['backend/Services/TrainingPlans/TrainingPlanWeekStructurePlanner.cs:11-29']
  },
  {
    id: 'structure.protective-bias',
    title: 'Protective bias tightens selected phase caps.',
    summary: 'Joint concern or age over 40 modifies the structure-cap table.',
    details: [
      'Protective bias is true when onboarding concerns include Joints or resolved age midpoint is greater than 40.',
      'Taper min rest days is at least 3.',
      'Base min rest days is at least 2, which matches the default Base value today.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWeekStructurePlanner.cs:42-43', 'backend/Services/TrainingPlans/TrainingPlanWeekStructurePlanner.cs:93-110']
  },
  {
    id: 'structure.cap.max-training-days',
    title: 'Max training-day cap removes support first, then light training days.',
    summary: 'Training-day remediation prefers deleting support days before reducing primary training work.',
    details: [
      'When CountTrainingDays exceeds the phase cap, the planner first chooses the lightest Support day.',
      'If no Support day is available, it chooses the lightest non-milestone, non-long Training day.',
      'The selected day is converted to rest and the loop repeats until the cap is satisfied or no candidate remains.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWeekStructurePlanner.cs:385-417']
  },
  {
    id: 'structure.cap.min-rest-days',
    title: 'Minimum rest-day cap removes support first, then light training days.',
    summary: 'When a week lacks enough rest, remediation creates rest days from the lightest eligible training days.',
    details: [
      'For partial weeks, required rest is ceiling(phase min rest days * daysInWeek / 7).',
      'The planner first converts the lightest Support day to rest.',
      'If no Support day is available, it converts the lightest non-milestone, non-long Training day.',
      'The loop repeats until CountRestDays satisfies the scaled minimum or no candidate remains.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWeekStructurePlanner.cs:61-65', 'backend/Services/TrainingPlans/TrainingPlanWeekStructurePlanner.cs:419-452']
  },
  {
    id: 'structure.cap.taper-rest',
    title: 'Taper weeks have an extra rest floor.',
    summary: 'Taper rest is enforced even after the phase min-rest calculation.',
    details: [
      'Full taper weeks require at least 2 rest days.',
      'Partial taper weeks require at least 1 rest day.',
      'This is enforced in addition to the resolved phase caps and protective-bias taper cap.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWeekStructurePlanner.cs:66-70', 'backend/Services/TrainingPlans/TrainingPlanWeekStructurePlanner.cs:803-807']
  },
  {
    id: 'structure.validation',
    title: 'Generation fails if remediated weeks still violate structure rules.',
    summary: 'Structure remediation is not best-effort only; a final validation can abort generation.',
    details: [
      'Validation checks expected date count and missing dates.',
      'Milestone dates cannot mix milestone and non-milestone workouts.',
      'Total training days, rest days, and taper rest must satisfy caps.',
      'Rest and support days must not leak distance, elevation, or pack.',
      'If validation fails, generation returns Success false with the validation error.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWeekStructurePlanner.cs:744-850', 'backend/Services/TrainingPlans/TrainingPlanGenerator.cs:332-341']
  },
  {
    id: 'support.balance.week2',
    title: 'Balance support starts in week 2 and should appear weekly.',
    summary: 'After structure remediation, missing balance work is re-added or given a low-load distance host when safe.',
    details: [
      'Starting week 2, if the week has no Balance exercise, the planner searches for an eligible stack host.',
      'If a host exists, balance exercises are added to that training day.',
      'If no host exists but the caller permits added volume and the week still has rest-day, phase-cap, and selected-day headroom, the planner creates a low-load distance host and adds balance there.',
      'Before prescriptions are seeded, final generated bundles are split so balance appears as its own support workout card.',
      'Generated-plan final cleanup stacks onto existing safe hosts and skips synthetic low-load distance hosts so support repair cannot inflate the settled distance contract.',
      'If no host can be used or safely created, a dropped-support trace is recorded.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs:440-540']
  },
  {
    id: 'support.lunge.week3',
    title: 'Lunge support starts in week 3 and should appear weekly.',
    summary: 'After structure remediation, missing lunge work is re-added or given a low-load distance host when safe.',
    details: [
      'Starting week 3, if the week has no Lunge exercise, the planner searches for an eligible stack host.',
      'Lunges use suppressJumpTrack when the user has knee/joint concerns.',
      'If no host exists but the caller permits added volume and the week still has rest-day, phase-cap, and selected-day headroom, the planner creates a low-load distance host outside protected milestone windows and adds lunge there.',
      'Before prescriptions are seeded, final generated bundles are split so lunge appears as its own support workout card.',
      'Generated-plan final cleanup stacks onto existing safe hosts and skips synthetic low-load distance hosts so support repair cannot inflate the settled distance contract.',
      'If no host can be used or safely created, a dropped-support trace is recorded.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs:540-620']
  },
  {
    id: 'support.stack-target',
    title: 'Stacked support needs an eligible training-day host.',
    summary: 'Balance and lunge work prefer eligible existing training hosts, with a narrow low-load distance fallback.',
    details: [
      'The host must be a Training day.',
      'The host cannot be a milestone.',
      'The host must have distance or elevation target.',
      'The host primary must be an approved active-catalog code: hike, run, stair climb, stair machine, step-ups, treadmill, or hill repeats.',
      'Lunge cannot stack on the long workout.',
      'Lunge cannot stack inside a protected milestone window.',
      'A host cannot already contain the same support category.',
      'When no eligible existing host remains, the planner may convert an empty rest day into a low-load run/hike/treadmill distance host if the caller permits added volume and phase, selected-day, and minimum-rest caps still allow it.',
      'After elevation concentration cleanup, lunge work is moved off capped, protected, milestone, or high-load hosts; if no lower-load host remains, the same low-load distance fallback can preserve it only for callers that explicitly permit added volume.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs:610-1050', 'backend/Services/TrainingPlans/TrainingPlanExerciseSemanticSanitizer.cs:378-490']
  },
  {
    id: 'support.repair-misstack',
    title: 'Mis-stacked support is moved, dropped, or converted to rest.',
    summary: 'If support lands on a non-stackable primary, the planner repairs it after structure changes.',
    details: [
      'Support exercises on workouts without an approved stackable primary are removed from the source workout.',
      'If the source workout becomes empty and convertEmptySourceToRest is true, the source becomes a rest day.',
      'The support category is moved to the nearest eligible host when one exists.',
      'If no eligible host exists, the support work is dropped and traced.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs:667-772']
  },
  {
    id: 'support.progression-freeze',
    title: 'Support progression freezes in the final two weeks.',
    summary: 'Generated support levels retain their earned position during the final two weeks.',
    details: [
      'A week is in the final two weeks when weekNumber >= totalWeeks - 1.',
      'Weekly generation and standalone lunge replacement use feedback before the final two weeks, preserving the level already earned without adding a new advancement in that period.',
      'Completed balance workouts still rewrite future balance exercises from their per-track feedback state.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs', 'backend/Services/TrainingPlans/TrainingPlanLungeWorkoutUtilities.cs']
  },
  {
    id: 'support.rating-adjustment',
    title: 'Consecutive difficulty ratings adjust support progression.',
    summary: 'Two consecutive TooEasy ratings advance a support category; JustRight holds its earned level.',
    details: [
      'Only completed prior workouts containing the requested category are considered, in date order.',
      'Exercise-level category ratings override workout-level ratings.',
      'Each consecutive pair of TooEasy category workouts earns one forward step; a later JustRight rating holds that earned step and resets the pair count.',
      'Each consecutive pair of TooHard category workouts steps back once.',
      'Mixed ratings and missing feedback do not earn a forward step.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs']
  },
  {
    id: 'support.balance-tracks',
    title: 'Balance support uses three tracks for two timed sets.',
    summary: 'Each assignment keeps one knee, one hip, and one tandem exercise while prescribing two complete rounds.',
    details: [
      'Knee track: BKT3, BKT4, BKT5, BKT6. Legacy BKT1 and BKT2 prescriptions remain valid in saved plans but are not selected for new plans.',
      'Hip track: BHT1, BHT2, BHT3, BHT4, BHT5.',
      'Tandem track: BTT1, BTT2, BTT3, BTT4, BTT5.',
      'Every selected exercise is prescribed as 2 sets with 1 minute of work and 15 seconds of rest.',
      'Calendar weeks alone do not advance a balance track.',
      'Each track advances one step after two consecutive completed TooEasy ratings on that track; JustRight resets that streak and holds the current exercise.',
      'A completed TooHard rating steps its track back by one; all track indexes remain clamped to their bounds.'
    ],
    sources: [
      'backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs',
      'mobile/lib/balanceWorkout.ts',
      'Codified rule id: support.balance-tracks'
    ]
  },
  {
    id: 'support.lunge-sequence',
    title: 'Lunge support uses a fixed two-exercise sliding window.',
    summary: 'New lunge assignments select exactly two consecutive exercises, starting with Reverse Lunge and Walking Lunge.',
    details: [
      'Sequence: BWTS, BWT1, BWT2, BWT3, BWT4, BWT5, PT2, PT3, PT4, JT1, JT2, JT3, JT4, JT5.',
      'Base index is 2 plus the cumulative feedback-earned progression adjustment; calendar weeks alone do not advance it.',
      'Base index is clamped so two exercises are always available.',
      'The initial window is BWT2 Reverse Lunge plus BWT3 Walking Lunge; the next progression window is BWT3 Walking Lunge plus BWT4 Reverse Slide Lunge.',
      'When jump track is suppressed, base index is capped at 6.',
      'BWT3, PT3, and JT exercises use code-specific rep overrides; remaining PT/JT exercises use 10 reps and other lunges use 15 reps.',
      'PT exercises use pack; non-PT/JT bodyweight and jump track exercises do not use pack.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs:265-321', 'Codified rule id: support.lunge-sequence']
  }
]

export const MILESTONE_RULES: CodifiedPlanRule[] = [
  {
    id: 'plan.short-window-day-hike',
    title: 'Four-to-six-week day-hike plans provide preparation with explicit limits.',
    summary: 'Dated DayHike goals with one itinerary day and 28–48 days available use a bounded 75% milestone schedule and a persistent mobile preparation notice.',
    details: [
      'Short plans retain the final canonical weeks, weekly distribution, progression rules, and recovery windows.',
      'The elevation milestone aims for 75% of itinerary elevation in week N-2. With at least 35 days available, a distance milestone aims for 75% of itinerary distance in week N-3.',
      'These milestones bypass the standard six-week and week-four gates. They replace the standard eight-milestone schedule, including its taper sharpener.',
      'Milestones retain their 75% goal targets without the pre-milestone weekly budget or ordinary elevation cap. The weekly distance ceiling and achievable grade still apply.',
      'Short-window goals use the same exemption from ordinary session caps as standard milestones.',
      'Short-window elevation milestones remain one workout; the standard high-elevation splitting policy does not apply.',
      'Unavailable milestone weeks are skipped instead of moving the workload into another week. Unsupported targets are reduced or omitted and produce generation warnings.',
      'The planner notice explains that completing this preparation plan does not establish readiness for the hike. Proxy goals, other profiles, and other time windows retain standard behavior.'
    ],
    sources: [
      'backend/Services/TrainingPlans/ShortWindowDayHikePolicy.cs',
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs',
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs',
      'mobile/components/planner/MyPlanTab.tsx'
    ]
  },
  {
    id: 'milestone.minimum-weeks',
    title: 'Milestones require a minimum plan length.',
    summary: 'Standard milestones require a minimum plan length; eligible short-window day hikes use their own bounded schedule.',
    details: [
      'Default MinimumWeeksForMilestones is 6.',
      'Outside the short-window day-hike policy, weeks.Count below the configured minimum skips milestone generation.',
      'A warning records the skip reason.'
    ],
    sources: ['backend/Config/MilestoneConfig.cs:26-30', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:35-39']
  },
  {
    id: 'milestone.requires-itinerary',
    title: 'Milestones require a usable hardest itinerary day.',
    summary: 'Milestones are skipped when hardest distance and hardest elevation cannot be determined.',
    details: [
      'Standard milestones require at least 8 milestone definitions after sorting. The short-window day-hike policy supplies its own one or two definitions.',
      'Milestones require resolvedGoal.Itinerary.Count > 0.',
      'Hardest day data is max distance day and max elevation day, which can be different itinerary days.',
      'If both max distance and max elevation are zero, milestones are skipped.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:41-68', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:110-120']
  },
  {
    id: 'milestone.schedule-table',
    title: 'Default milestones are scheduled relative to plan end.',
    summary: 'The default milestone table has eight definitions, with focus and percentage fixed by milestone number.',
    details: [
      'Milestone week resolves as totalWeeks - WeeksFromEnd.',
      'Milestones 1, 3, 5, 7, and 8 are elevation focus by default.',
      'Milestones 2, 4, and 6 are distance focus by default.',
      'Milestone 8 is the taper sharpener at 50% one week from the end.'
    ],
    table: {
      columns: ['Milestone', 'Week', 'Focus', 'Percent'],
      rows: [
        ['1', 'N-8', 'Elevation', '70%'],
        ['2', 'N-7', 'Distance', '70%'],
        ['3', 'N-6', 'Elevation', '75%'],
        ['4', 'N-5', 'Distance', '75%'],
        ['5', 'N-4', 'Elevation', '90%'],
        ['6', 'N-3', 'Distance', '90%'],
        ['7', 'N-2', 'Elevation', '100%'],
        ['8', 'N-1', 'Elevation', '50%']
      ]
    },
    sources: ['backend/Config/MilestoneConfig.cs:37-113', 'backend/Config/MilestoneConfig.cs:145-153']
  },
  {
    id: 'milestone.minimum-week',
    title: 'Standard milestones cannot land before the configured milestone start week.',
    summary: 'A standard milestone below MilestoneMinimumWeek is skipped; eligible short-window day hikes use their own offsets.',
    details: [
      'Default MilestoneMinimumWeek is 4.',
      'Admin validation allows MilestoneMinimumWeek from 1 to 20.',
      'If the resolved milestone week is below max(1, MilestoneMinimumWeek), the milestone is skipped with a warning.'
    ],
    sources: ['backend/Models/TrainingPlans/PlanGenerationConfig.cs:108-119', 'backend/Services/TrainingPlans/PlanGenerationConfigService.cs:346-350', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:136-146']
  },
  {
    id: 'milestone.phase-eligible',
    title: 'Only Base, Build, Peak, and Taper weeks can host milestones.',
    summary: 'PreBase weeks never host milestone workouts.',
    details: [
      'If a milestone resolves to an ineligible phase, it is skipped with a warning.',
      'Taper is eligible because milestone 8 can land at N-1.',
      'Blackout relocation also searches only milestone-capable weeks.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:148-153', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:1055-1066']
  },
  {
    id: 'milestone.blackout-relocation',
    title: 'Milestones move around fully unavailable weeks.',
    summary: 'Standard milestones on blackout weeks relocate to a nearby capable week; short-window day-hike milestones are omitted for that week.',
    details: [
      'A blackout target week is one with BlackoutWeek flag or zero workouts.',
      'The relocation search looks for a week with workouts, no existing milestone, eligible phase, and week number at or after MilestoneMinimumWeek.',
      'Nearest eligible week wins; earlier weeks are preferred at equal offset.',
      'If no nearby capable week exists, the milestone is skipped with a warning.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:155-169', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:1067-1100']
  },
  {
    id: 'milestone.host-selection',
    title: 'Milestones replace an existing host workout.',
    summary: 'A milestone does not create from nothing; it selects a host workout in the target week.',
    details: [
      'The preferred host is the long workout.',
      'Fallback host is the latest eligible candidate in the week.',
      'A host candidate cannot already be a milestone.',
      'Eligible candidates include load/long/distance/elevation/pack workouts or workouts with cardio/elevation/HIIT exercises.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:171-179', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:247-275']
  },
  {
    id: 'milestone.end-of-week',
    title: 'Milestone host workouts move to the end of the week.',
    summary: 'Milestones are placed late in the week, respecting preferred days when possible.',
    details: [
      'If Sunday is preferred, target Sunday.',
      'Otherwise if Saturday is preferred, target Saturday.',
      'Otherwise use the latest preferred day in the week.',
      'Otherwise use the latest existing training day.',
      'Otherwise use the week end date.',
      'If the target date is occupied, the planner swaps dates with the existing workout.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:277-351']
  },
  {
    id: 'milestone.target-percentage',
    title: 'Milestone targets and Coach Notes use hardest-day percentages.',
    summary: 'Milestone definitions select a hardest-day percentage, and Coach Notes describe the final aggregate milestone relationship without inventing an itinerary-day rank.',
    details: [
      'The default milestone table uses equal min/max percentages, so defaults are fixed percentages.',
      'The planner calls GetPercentageForFitness(fitnessLevel) for each milestone.',
      'The chosen percentage multiplies hardest-day distance for distance milestones or hardest-day elevation for elevation milestones.',
      'Weekly Coach Notes sum every matching same-day milestone component before comparing the final distance target with the longest itinerary day or the final elevation target with the hardest climbing day.',
      'The rounded displayed percentage is derived from the final aggregate, so split rounding and a hard distance clamp remain visible; Coach Notes do not rank a single split component against the nearest itinerary day.'
    ],
    sources: [
      'backend/Config/MilestoneConfig.cs:160-190',
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:238-259',
      'backend/Services/TrainingPlans/TrainingPlanWeeklyCoachNotePlanner.cs:597-679'
    ]
  },
  {
    id: 'milestone.distance-focus',
    title: 'Distance milestones zero elevation and prescribe distance work.',
    summary: 'A distance-focus milestone is not a mixed hike; it is a pure distance target.',
    details: [
      'Target distance is hardest-day max distance multiplied by the milestone percentage.',
      'Target elevation is set to 0.',
      'The milestone primary exercise is constrained to an existing distance code or HIKE/RUN fallback.',
      'Original weekly elevation is redistributed to other eligible non-milestone workouts.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:188-191', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:832-900', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:1155-1199']
  },
  {
    id: 'milestone.elevation-focus',
    title: 'Elevation milestones carry grade-cap distance and prescribe elevation work.',
    summary: 'An elevation-focus milestone keeps its full elevation target and carries just enough distance to keep the climb within the workout grade cap.',
    details: [
      'Target elevation is hardest-day max elevation multiplied by the milestone percentage; the grade cap never reduces it.',
      'Target distance is elevation divided by the fitness-band grade cap, so the prescribed steepness stays achievable (previously distance was set to 0).',
      'A workout.grade-cap trace entry records the assigned carry distance.',
      'A milestone that stays a single workout keeps carry distance only when its exercise contributes distance; stair-climb, stair-machine, and step-up hosts drop it so the final sync does not add a second exercise.',
      'Original weekly distance is redistributed to other eligible non-milestone workouts.',
      'Elevation milestones can split into multiple same-day milestone components.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:193-228', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:614-735', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:1155-1199']
  },
  {
    id: 'milestone.clamp-week-capacity',
    title: 'Milestones retain goal-based targets within the weekly distance ceiling.',
    summary: 'Milestones are not limited to the original host-week workload. Short-window day hikes retain their goal-based challenge within the distance ceiling and achievable grade.',
    details: [
      'The milestone percentage is applied to the hardest itinerary day before clamping.',
      'Distance targets use the effective weekly ceiling: 30 miles or a lower configured limit. The historical 48.28 km setting permits 30 full miles.',
      'For standard plans, this cap does not reduce distance milestones to the host workout distance or original week total, and elevation is not clamped by original week capacity.',
      'Short-window day hikes use the 75% goal target without a host-week budget or ordinary elevation cap; achievable grade and the weekly distance ceiling still apply.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs', 'backend/Services/TrainingPlans/TrainingPlanDistanceCeiling.cs']
  },
  {
    id: 'milestone.pack-weight',
    title: 'Milestones use the week pack prescription.',
    summary: 'Milestone pack weight resolves from weekly prescribed/target pack before falling back to workout pack.',
    details: [
      'The planner first uses week.PrescribedPackWeightKg when present.',
      'Then it uses week.TargetAveragePackWeightKg when positive.',
      'Then it falls back to the original workout PackWeightKg.',
      'The resolved value is rounded to two decimals.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:200-208', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:1142-1153']
  },
  {
    id: 'milestone.remove-addons',
    title: 'Milestones remove balance, lunge, and HIIT add-ons.',
    summary: 'Milestone days keep primary training work only.',
    details: [
      'When a workout becomes a milestone, Balance, Lunge, and HIIT exercises are removed.',
      'Remaining exercises are re-ordered.',
      'If no HIIT remains, IntensityModality is set to None.',
      'Assignment rounding later also keeps only primary cardio/elevation exercises on milestone workouts.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:1029-1053', 'backend/Services/TrainingPlans/TrainingPlanAssignmentRounding.cs:420-447']
  },
  {
    id: 'milestone.redistribute-week',
    title: 'Milestones rebalance the week after replacing the host.',
    summary: 'Milestone load is not additive; remaining distance is redistributed and elevation is rebalanced across eligible same-week hosts.',
    details: [
      'After milestone target assignment, the planner redistributes remaining original distance/elevation to eligible non-milestone training workouts.',
      'Milestone workouts are excluded from redistribution targets.',
      'Added elevation raises the least-loaded hosts toward the next load level before any one host receives more, subject to each workout capacity.',
      'When the remaining elevation target is lower than the current non-milestone load, existing elevation is reduced proportionally across eligible hosts.',
      'Successful elevation redistribution emits paired before/after workout contexts under milestone.redistribute-week.',
      'Distance redistribution retains its existing ordered remainder behavior.',
      'Estimated durations are refreshed after redistribution.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs']
  },
  {
    id: 'milestone.elevation-split-count',
    title: 'Elevation milestone split count is determined by elevation thresholds.',
    summary: 'Elevation milestones split into two, three, or four same-day components.',
    details: [
      'If elevation is below TwoWorkoutMaxElevationMeters, split into 2 workouts.',
      'If elevation is below ThreeWorkoutMaxElevationMeters, split into 3 workouts.',
      'At or above ThreeWorkoutMaxElevationMeters, split into 4 workouts.',
      'Defaults are about 2,000 ft and 3,500 ft (609 m and 1,067 m).'
    ],
    table: {
      columns: ['Elevation target', 'Component count'],
      rows: [
        ['< 2,000 ft (609 m)', '2'],
        ['>= 2,000 ft and < 3,500 ft (609 m to 1,067 m)', '3'],
        ['>= 3,500 ft (1,067 m)', '4']
      ]
    },
    sources: ['backend/Models/TrainingPlans/PlanGenerationConfig.cs:335-367', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:637-642']
  },
  {
    id: 'milestone.elevation-split-lead',
    title: 'Elevation milestone lead component carries the configured lead fraction.',
    summary: 'The first split component is special and uses true-elevation options when available.',
    details: [
      'Default lead fraction is 0.30.',
      'Lead codes come from true-elevation access: HIKE, HILL_REPEATS, STAIR_CLIMB, STEP_UPS.',
      'If no true-elevation lead code is available, HIKE is used as the lead candidate.',
      'If no eligible lead definition exists, the milestone remains a single workout and a warning is emitted.'
    ],
    sources: ['backend/Models/TrainingPlans/PlanGenerationConfig.cs:350-356', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:644-662', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:681-688']
  },
  {
    id: 'milestone.elevation-split-components',
    title: 'Elevation split balance components prefer true elevation, then artificial options, and may repeat.',
    summary: 'The remaining same-day components divide the non-lead elevation.',
    details: [
      'Balance pool starts with true-elevation codes other than the lead.',
      'Artificial options are appended after true-elevation options: STAIR_MACHINE, TREADMILL.',
      'If the balance pool is empty, the lead code repeats.',
      'Balance elevation is divided equally except the last component absorbs remainder.',
      'Each distance-bearing component carries enough distance to keep its climb within the grade cap; treadmill components use the lower of the band cap and the max-incline-implied grade.',
      'Each component is inserted as a separate same-day milestone workout card.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:664-735']
  },
  {
    id: 'milestone.prehike.schedule',
    title: 'Pre-hike milestone is scheduled by date when configured.',
    summary: 'Pre-hike milestone logic uses target hike date minus the configured offset.',
    details: [
      'Default PreHikeMilestoneOffsetDays is 8.',
      'Default PreHikeMilestonePercentage is 0.50.',
      'The planner finds the closest existing workout to targetHikeDate minus offset.',
      'If the closest workout is already a milestone, pre-hike milestone is skipped.',
      'The pre-hike milestone is hidden from the user and marked UserChoice focus.'
    ],
    sources: ['backend/Config/MilestoneConfig.cs:14-24', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:936-1027']
  },
  {
    id: 'milestone.prehike.user-choice',
    title: 'Pre-hike milestone chooses distance or elevation by largest delta.',
    summary: 'The hidden pre-hike milestone stores UserChoice but initially zeros one side based on target delta.',
    details: [
      'Pre-hike target distance is 50% of hardest-day distance by default.',
      'Pre-hike target elevation is 50% of hardest-day elevation by default.',
      'If user-choice focus resolves to Distance, elevation is set to 0.',
      'If user-choice focus resolves to Elevation, distance is set to 0.',
      'The selected focus is based on the larger delta from the existing closest workout.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:982-1019', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:1124-1140']
  },
  {
    id: 'milestone.window.before',
    title: 'Elevation milestone lead-in protection gets shorter for later challenges.',
    summary: 'The protected window system blocks nearby elevation/lunge/HIIT stress around elevation and user-choice milestones.',
    details: [
      'Only elevation and user-choice milestone workouts add protected dates.',
      'The first two standard elevation challenges (milestone IDs 1 and 3) protect offsets -2 and -1 days.',
      'The third and fourth standard elevation challenges (milestone IDs 5 and 7) create no pre-milestone protected dates.',
      'Other elevation milestones and user-choice milestones retain offsets -3, -2, and -1 days. Windows use the milestone identity even if earlier challenges are omitted or one challenge is split into components.',
      'Distance milestones do not create before- or after-milestone protected dates.',
      'Protected dates are used by milestone restriction and support-stacking eligibility.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestoneWindowRules.cs']
  },
  {
    id: 'milestone.window.after-elevation',
    title: 'Elevation and user-choice milestones protect one day after.',
    summary: 'Post-milestone protection applies only to elevation-like milestone stress.',
    details: [
      'The same elevation/user-choice focus check gates the after-milestone window.',
      'Offset +1 day is protected for elevation and user-choice milestones.',
      'Distance milestones do not create protected dates.',
      'The protected date set is computed from all milestone workouts in the plan.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestoneWindowRules.cs']
  },
  {
    id: 'milestone.window.restrict-workouts',
    title: 'Protected-window workouts keep distance but lose elevation stress.',
    summary: 'Elevation, HIIT, and lunge stress are removed around elevation-like milestones while distance-only work may remain.',
    details: [
      'Non-milestone workouts on protected dates have elevation set to 0 and ElevationType set to None.',
      'IntensityModality is set to None.',
      'If distance remains, the day becomes distance load with controlled-effort completion criteria and prefers RUN before HIKE.',
      'If no distance remains but balance exercises remain, the day becomes SupportBalance.',
      'If neither distance nor balance remains, the day converts to rest.',
      'Removed elevation is redistributed to eligible non-protected load/elevation workouts in the same week.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:353-488']
  },
  {
    id: 'milestone.debt-created',
    title: 'Unfit milestone load becomes metric debt.',
    summary: 'When milestone or protected-window redistribution cannot safely fit in the same week, the missing distance or elevation is tracked instead of silently disappearing.',
    details: [
      'Debt can be created when same-week non-milestone workouts do not have enough distance or elevation capacity.',
      'Each debt records the source week, source rule id, metric, amount, reason, and candidate attempts.',
      'Distance debt keeps kilometer precision; elevation debt is rounded to whole meters.',
      'The target waterfall captures a dedicated debt-created stage with created distance and elevation notes.'
    ],
    sources: [
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:454-462',
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:1219-1288',
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:1516-1546',
      'backend/Services/TrainingPlans/MilestoneMetricDebt.cs:32-150'
    ]
  },
  {
    id: 'milestone.debt-repaid',
    title: 'Milestone metric debt is repaid into nearby eligible weeks.',
    summary: 'Debt repayment preserves as much milestone/protected-window load as possible without violating nearby-week constraints.',
    details: [
      'Repayment tries the source week, previous week, and next week first, then other eligible Base, Build, Peak, or taper-compatible weeks.',
      'Distance debt prefers distance-capable training workouts; elevation debt avoids milestone days and protected-window dates.',
      'Distance repayment is still bounded by weekly distance capacity; elevation repayment has no daily or weekly elevation cap.',
      'Trace contexts list the week/date where repayment was applied.'
    ],
    sources: [
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:1548-1611',
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:1630-1703',
      'backend/Services/TrainingPlans/MilestoneMetricDebt.cs:156-195'
    ]
  },
  {
    id: 'milestone.debt-unpaid',
    title: 'Unpaid milestone debt remains visible after caps and rounding.',
    summary: 'Any debt that cannot fit, or is later removed by final rounding and weekly ceilings, is reported for review.',
    details: [
      'A warning is emitted when eligible repayment capacity is exhausted.',
      'After assignment rounding and post-round weekly ceilings, repayments are reconciled against final workouts.',
      'If rounding or weekly ceilings remove repaid load, the lost amount becomes unpaid debt and gets its own warning/trace.',
      'The target waterfall includes an unpaid accounting stage so reviewers can see what load could not be preserved.'
    ],
    sources: [
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs:479-490',
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:1612-1620',
      'backend/Services/TrainingPlans/MilestoneMetricDebt.cs:78-116',
      'backend/Services/TrainingPlans/MilestoneMetricDebt.cs:197-283'
    ]
  },
  {
    id: 'milestone.day-exclusive',
    title: 'Milestone days cannot mix milestone and non-milestone workouts.',
    summary: 'Week normalization and validation keep milestone dates exclusive.',
    details: [
      'When duplicate workouts exist on a date and any are milestones, milestone workouts are kept for that date.',
      'Validation fails if a date group contains both milestone and non-milestone workouts.',
      'This allows split elevation milestone components to share a date while excluding ordinary work from that date.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWeekStructurePlanner.cs:163-177', 'backend/Services/TrainingPlans/TrainingPlanWeekStructurePlanner.cs:772-780']
  },
  {
    id: 'regeneration.completed-workout-carry-forward',
    title: 'Regeneration preserves the active goal, calendar, pack target and completed history.',
    summary: 'Date, preference and Coach rebuilds retain settings unless explicitly changed, preserve completed history, and save preferences with the replacement plan only after success.',
    details: [
      'Plan regeneration builds the replacement from the active plan’s original start date, so changing preferences in week 4 leaves the user in week 4.',
      'The active plan supplies the goal identity, beginner/proxy label and level, target date and pack target; onboarding history cannot replace these inputs.',
      'Without an explicit date edit, start/end dates, duration and partial-first-week boundaries remain fixed, including undated custom-duration plans.',
      'An explicit date or pack-weight edit changes that target; mobile preferences submit only changed fields.',
      'The Planner date action rebuilds the same goal for the new departure date. Proposed preferences are evaluated in memory; a rejected, interrupted or failed rebuild leaves the saved date, preferences and active plan unchanged.',
      'After the replacement plan is stored, its active-plan reference and edited preferences are published together in one user-document update.',
      'A completed prescribed workout can replace one generated prescribed workout on the same date.',
      'Unmatched completed prescriptions and every recorded workout import are carried into the matching regenerated week as separate workouts.',
      'Completed-workout record links, actuals, notes, exercises, and provenance are retained.',
      'Recorded imports continue to contribute only to actual progress and remain excluded from regenerated targets.',
      'The carry-forward stamps regeneration provenance and emits trace contexts for the generated workout, previous completed workout, and final preserved workout.'
    ],
    sources: [
      'backend/Services/TrainingPlans/TrainingPlanRegenerationUtilities.cs',
      'backend/Services/TrainingPlans/TrainingPlanRegenerationService.cs'
    ]
  }
]

export const ADAPTATION_MUTATION_RULES: CodifiedPlanRule[] = [
  {
    id: 'generation.existing-plan-retry',
    title: 'Ordinary generation retries preserve the active plan.',
    summary: 'A stale onboarding route or repeated request returns the existing active plan unless the user explicitly requested a replan.',
    details: [
      'The mobile generation request marks deliberate replans explicitly.',
      'When an active plan already exists, an ordinary generation retry returns that plan without inserting another plan or changing its workouts.',
      'The retry returns the same visible targets, active goal baselines, revision states, workout identities, and workout values.',
      'A short-lived per-user generation lease rejects overlapping requests before either can insert a plan.',
      'Explicit replans continue through normal generation and may replace the active plan.',
      'This protects logout/login and lost-response retries without blocking intentional goal changes.'
    ],
    sources: [
      'HIK-723',
      'backend/Controllers/MobileTrainingPlanController.cs',
      'backend/DTOs/MobileTrainingPlan/MobileTrainingPlanDtos.cs',
      'mobile/lib/trainingPlanGeneration.ts'
    ]
  },
  {
    id: 'adaptation.uniform-redistribution-toggle',
    title: 'Automatic recovery reductions are disabled by default.',
    summary: 'HSI scoring remains active. The legacy uniform-redistribution setting now gates only TooHard remaining-plan reductions; it cannot enable catch-up or high-HSI increases.',
    details: [
      'New, fallback, and reset HSI policies default EnableUniformRedistribution to false.',
      'While disabled, workout completion does not run the TooHard remaining-plan reduction. Catch-up and high-HSI workload increases are disabled regardless of the stored setting.',
      'Survey elevation throttling and balance-support progression are separate completion behaviors and remain active.',
      'Changing the default does not rewrite existing active policies or revert plans that were adjusted previously.'
    ],
    sources: [
      'HIK-790',
      'backend/Models/TrainingPlans/HikeStrongIndexConfig.cs',
      'backend/DTOs/TrainingPlans/HikeStrongIndexConfigDto.cs',
      'backend/Services/TrainingPlans/TrainingPlanWorkoutCompletionService.cs'
    ]
  },
  {
    id: 'mutation.active-revision-baseline',
    title: 'Visible goals use an active system baseline while the original prescription remains immutable.',
    summary: 'Automatic plan changes become Goal; only differences introduced after that active baseline appear Revised.',
    details: [
      'Initial generated targets, workout shape, and support-workout counts remain stored as the immutable original prescription.',
      'Automatic ceiling enforcement, throttling, reductions, and balance progression update the active baseline only for weeks that were not already user-revised.',
      'Manual add, edit, move, and delete operations compare against that active baseline and immediately show Revised.',
      'Workout dates participate in the signature, so moving a prescription is visible even when its load is unchanged.',
      'Recorded workout imports are excluded from the prescription signature and cannot hide a deleted or moved planned workout.',
      'Mobile exposes active distance, elevation, pack, weighted-workout, lunge, balance, and HIIT goal values; admin detail and export additionally expose their immutable originals.'
    ],
    sources: [
      'HIK-790',
      'backend/Models/TrainingPlans/GeneratedTrainingPlan.cs',
      'backend/Services/TrainingPlans/TrainingPlanMutationUtilities.cs',
      'mobile/components/WeeklyProgressCard.tsx',
      'dashboard/src/pages/MobileUserDetailPage.tsx'
    ]
  },
  {
    id: 'mutation.revised-effective-week',
    title: 'Revised combines completed actuals with the remaining weekly schedule.',
    summary: 'The mobile Revised column is an effective projection, not merely the stored sum of planned targets.',
    details: [
      'Completed assigned workouts contribute recorded distance and elevation, falling back to assigned values only when an actual is absent.',
      'Completed unassigned workouts recorded through Track contribute their recorded actuals.',
      'Incomplete workouts scheduled for the app local date or later contribute their assigned values.',
      'Incomplete workouts before the app local date are missed and contribute zero.',
      'Weighted, lunge, balance, and HIIT workout counts use the same completed-or-still-scheduled eligibility rule.',
      'The Revised column appears when this effective projection differs from the active Goals baseline, even if the prescription signature itself remains Goal.'
    ],
    sources: [
      'HIK-877',
      'mobile/lib/trainingPlanTotals.ts',
      'mobile/components/WeeklyProgressCard.tsx',
      'mobile/components/planner/MyPlanTab.tsx'
    ]
  },
  {
    id: 'mutation.concurrent-writers',
    title: 'Concurrent planner writes for one user are serialized.',
    summary: 'Completion, import, manual mutation, generation, and regeneration cannot overwrite one another inside the running API instance.',
    details: [
      'All whole-plan read-modify-replace paths acquire the same user-keyed mutation coordinator.',
      'Concurrent wearable retries therefore produce one imported record and one duplicate result.',
      'A completion racing a manual add preserves both durable changes.',
      'Pending workouts in newly persisted generated plans receive fresh workout GUIDs, while carried completed-workout identities remain stable for record linkage; delayed pending-workout IDs are rejected instead of mutating same-date replacements.',
      'Workout lookup remains scoped to the authenticated user active plan; another user cannot mutate the owner plan.'
    ],
    sources: [
      'backend/Services/TrainingPlans/TrainingPlanMutationCoordinator.cs',
      'backend/Services/TrainingPlans/TrainingPlanWorkoutCompletionService.cs',
      'backend/Services/TrainingPlans/TrainingPlanWorkoutMutationService.cs',
      'backend/Services/TrainingPlans/TrainingPlanRegenerationService.cs',
      'backend/Services/Workouts/WorkoutRecordService.cs',
      'backend/Controllers/MobileTrainingPlanController.cs'
    ]
  },
  {
    id: 'hsi.score.weights',
    title: 'HSI score weights distance, elevation, and pack.',
    summary: 'The HikeStrong Index compares actual completed load to planned load through the single active, structurally valid HSI config.',
    details: [
      'Production requires exactly one active HSI config; missing, duplicate, or invalid active policy makes readiness unavailable.',
      'Admin activation and every calculation use the same structural validator, so legacy or malformed policy documents cannot silently take effect.',
      'Automatic default seeding and the instrumented in-memory fallback are limited to Development and Testing.',
      'Default weights are distance 0.40, elevation 0.40, pack 0.20.',
      'Each metric index is actual divided by planned, clamped by config.',
      'Recorded workout imports contribute to completed actual load but are excluded from the planned-load denominator.',
      'Deleting an incomplete missed workout removes its prescription from historical planned load and immediately refreshes the persisted HSI snapshot.',
      'Weights for zero-plan metrics can be dropped depending on zero-plan mode.',
      'Remaining weights are normalized before computing weighted score percent.'
    ],
    sources: [
      'HIK-752',
      'backend/Services/TrainingPlans/HikeStrongIndexConfigValidator.cs',
      'backend/Services/TrainingPlans/HikeStrongIndexConfigService.cs',
      'backend/Services/TrainingPlans/HikeStrongIndexService.cs',
      'backend/Program.cs'
    ]
  },
  {
    id: 'hsi.score.zero-plan',
    title: 'HSI zero-plan metrics follow per-metric zero-plan modes.',
    summary: 'A planned metric of zero does not always mean a zero score.',
    details: [
      'Distance zero-plan default mode is UseEpsilon.',
      'Elevation zero-plan default mode is UseEpsilon.',
      'Pack zero-plan default mode is IgnoreInScore.',
      'TreatAsPerfect returns index 1.0, IgnoreInScore removes weight, UseEpsilon divides actual by epsilon and clamps.'
    ],
    sources: ['backend/Models/TrainingPlans/HikeStrongIndexConfig.cs:146-164', 'backend/Services/TrainingPlans/HikeStrongIndexService.cs:146-181']
  },
  {
    id: 'hsi.bands',
    title: 'HSI readiness bands are configurable with default ranges.',
    summary: 'The HSI percent maps to a display band used by readiness context and adaptations.',
    details: [
      'Default Off Track is 0-50.',
      'Default Behind Plan is 51-75.',
      'Default On Track is 76-99.',
      'Default Ahead of Plan is 100-150.',
      'Score percent is clamped to 0-150 by default.'
    ],
    table: {
      columns: ['Band', 'Percent range'],
      rows: [
        ['Off Track', '0-50'],
        ['Behind Plan', '51-75'],
        ['On Track', '76-99'],
        ['Ahead of Plan', '100-150']
      ]
    },
    sources: ['backend/Models/TrainingPlans/HikeStrongIndexConfig.cs:70-143', 'backend/Services/TrainingPlans/HikeStrongIndexService.cs:205-241']
  },
  {
    id: 'hsi.coach-notes-minimum-workouts',
    title: 'HSI unlocks only after five eligible plan completions.',
    summary: 'Weekly readiness copy and the mobile HSI card use the same active-plan gate based on metric-bearing prescribed workouts.',
    details: [
      'Before five prescribed workouts with distance, elevation, or pack targets are completed, the numeric HSI stays hidden and weekly coach notes use non-HSI readiness guidance.',
      'Recorded workout imports and completed zero-target support workouts do not unlock HSI, even though recorded imports can still contribute actual load after readiness is established.',
      'At five eligible active-plan completions, HSI score, band, and trend may be applied to current and future weekly notes.',
      'Premature HSI rationale fields are removed when readiness copy is rewritten.',
      'Mobile week responses also scrub premature HSI copy from older persisted plans.'
    ],
    sources: [
      'backend/Services/TrainingPlans/TrainingPlanWeeklyCoachNotePlanner.cs',
      'backend/Controllers/MobileTrainingPlanController.cs:171-178',
      'mobile/lib/hsi.ts:7-152'
    ]
  },
  {
    id: 'adaptation.survey-throttle',
    title: 'Consecutive TooHard elevation workouts throttle upcoming elevation.',
    summary: 'Survey throttle reduces near-future elevation after too many hard elevated completions.',
    details: [
      'Default threshold is 3 consecutive TooHard workouts.',
      'Only workouts with actual or planned elevation at least 500 ft (152.4 m) count by default.',
      'Default throttle percent is 20%.',
      'Default throttle duration is 2 weeks.',
      'Future workouts after the triggering date and within the throttle window have elevation multiplied by 1 - throttlePercent.',
      'Completed workouts and recorded workout imports are historical actuals and are never throttled.',
      'Workouts already throttled are skipped.',
      'Untouched weeks accept the throttled result as their active Goals-column baseline; user-revised weeks retain their earlier baseline.'
    ],
    sources: ['backend/Models/TrainingPlans/HikeStrongIndexConfig.cs:166-180', 'backend/Services/TrainingPlans/TrainingPlanAdaptationUtilities.cs:23-65', 'backend/Services/TrainingPlans/TrainingPlanAdaptationUtilities.cs:457-515']
  },
  {
    id: 'adaptation.too-hard-reduction',
    title: 'Two consecutive TooHard elevated workouts can reduce the remaining plan.',
    summary: 'The recovery engine can reduce remaining workload; it never follows a reduction with catch-up increases.',
    details: [
      'This rule runs only when the active policy explicitly enables uniform redistribution; new policies default it off.',
      'The hard-coded trigger is 2 consecutive TooHard elevated workouts.',
      'Each reduction is 10%.',
      'At most two reductions are applied, for a max of 20%.',
      'Taper weeks are excluded.',
      'Future workouts after the triggering date scale distance, elevation, and pack by the reduction multiplier.',
      'Only pending prescribed workouts are scaled; completed workouts and recorded imports remain unchanged.',
      'Untouched future weeks accept the reduction as their active Goals-column baseline; user-revised weeks remain Revised.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanAdaptationUtilities.cs:18-21', 'backend/Services/TrainingPlans/TrainingPlanAdaptationUtilities.cs:67-110', 'backend/Services/TrainingPlans/TrainingPlanAdaptationUtilities.cs:540-602']
  },
  {
    id: 'adaptation.no-catch-up',
    title: 'Missed workouts never become extra work later.',
    summary: 'Low HSI and missed or shorter workouts do not increase future distance, elevation, or pack prescriptions.',
    details: [
      'HSI continues to compare completed activity with prescribed activity to date; it reports adherence without creating repayment obligations.',
      'The legacy redistribution setting cannot re-enable catch-up, even for existing production policies.',
      'Completing more workouts cannot repeatedly charge the same cumulative shortfall.',
      'High HSI alone also cannot trigger automatic increases, advance simulations, or peak extensions. Additional workload requires an explicit user plan change.',
      'Recovery reductions and survey throttles remain available under their existing gates.',
      'Previously inflated plans are not regenerated automatically; pending distance is bounded when a new completion invokes the ceiling safeguard.'
    ],
    sources: ['HIK-1018', 'backend/Services/TrainingPlans/TrainingPlanAdaptationUtilities.cs', 'backend/Services/TrainingPlans/TrainingPlanWorkoutCompletionService.cs']
  },
  {
    id: 'adaptation.schedule-check-in',
    title: 'Repeated missed training days offer an optional schedule review.',
    summary: 'Two distinct missed primary training days in the previous 14 local calendar days show a Planner check-in linked to plan settings.',
    details: [
      'Today and future dates are excluded; multiple workout cards on one date count as one missed day.',
      'A day counts when at least one prescribed primary workout remains incomplete. Rest days, support-only work, recorded imports, manual plans, and finished plans do not trigger the check-in.',
      'The check-in is calculated from current workout records whenever the mobile plan is returned, independently of HSI and its display threshold.',
      'Opening settings does not change the plan. The user chooses whether to adjust their available schedule.',
      'The prompt clears when fewer than two qualifying missed days remain in the lookback window.'
    ],
    sources: ['HIK-1018', 'backend/Services/TrainingPlans/TrainingPlanScheduleCheckIn.cs', 'backend/DTOs/MobileTrainingPlan/MobileTrainingPlanDtos.cs', 'mobile/components/planner/MyPlanTab.tsx']
  },
  {
    id: 'adaptation.apply-caps',
    title: 'The distance ceiling also applies after generation.',
    summary: 'Pending prescriptions stay within 30 miles (48.28 km), or a lower configured limit, when completion applies the safeguard.',
    details: [
      'Completed prescriptions and past days reserve weekly capacity and are never rewritten; recorded imports remain historical actuals outside the prescribed limit.',
      'If history already exceeds the limit, it remains intact and no remaining distance capacity is available.',
      'Final workout and exercise distances, durations, and active goal baselines are synchronized after automatic reductions.',
      'Manual add and edit requests that increase an oversized week are rejected before persistence. Reducing an already oversized week is allowed.',
      'Generation, milestone placement, and generated-load redistribution use the same effective ceiling. This is a ceiling, not a target to fill.'
    ],
    sources: ['HIK-1018', 'backend/Services/TrainingPlans/TrainingPlanDistanceCeiling.cs', 'backend/Services/TrainingPlans/TrainingPlanAssignmentRounding.cs', 'backend/Services/TrainingPlans/TrainingPlanWorkoutCompletionService.cs', 'backend/Services/TrainingPlans/TrainingPlanWorkoutMutationService.cs']
  },
  {
    id: 'completion.balance-rewrite',
    title: 'Completing balance support rewrites future balance progression.',
    summary: 'Future incomplete balance workouts can be regenerated from completed balance track state.',
    details: [
      'After workout completion, if the completed workout should refresh balance support progression, all exercises are loaded.',
      'ReapplyBalanceSupportProgression walks workouts in date order.',
      'Completed balance workouts advance each track after two consecutive TooEasy ratings; JustRight holds the current level.',
      'Incomplete future balance support workouts at or after rewriteFromDate receive replacement balance exercises from the current progression state.',
      'Regeneration trace entries include before/after contexts when this rewrite runs during plan regeneration.',
      'Untouched future weeks accept rewritten balance exercises as their active goal baseline.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWorkoutCompletionService.cs', 'backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs']
  },
  {
    id: 'completion.lunge-rewrite',
    title: 'Completing lunge support updates upcoming lunge exercises.',
    summary: 'Incomplete future lunge workouts use the level earned through completed lunge feedback.',
    details: [
      'After a completed lunge workout, future incomplete lunge exercises are rebuilt from completed ratings while other exercise categories stay in place.',
      'Two consecutive TooEasy workouts move the lunge window forward one step; JustRight holds the current window.',
      'Recognized lunge exercises in older completed plans anchor the earned level when the plan is rewritten or regenerated.',
      'A future workout with the same lunge codes is left unchanged, preserving manual exercise details.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWorkoutCompletionService.cs', 'backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs', 'backend/Services/TrainingPlans/TrainingPlanRegenerationService.cs']
  },
  {
    id: 'mutation.no-past-edit',
    title: 'Past planned workouts cannot be edited, but missed workouts can be deleted.',
    summary: 'Planner mutation endpoints preserve past prescriptions except for an explicit deletion of an incomplete missed workout.',
    details: [
      'The local date supplied by the client is compared to the workout date.',
      'Add rejects a request date before localDate.',
      'Update rejects workout dates before localDate.',
      'Move rejects both source and target dates before localDate.',
      'Delete allows an incomplete workout before localDate so the user can remove a missed prescription; completed workout deletion remains a separate recorded-activity flow.',
      'Missed deletion removes the prescription from HSI planned history without redistributing future workouts.'
    ],
    sources: [
      'HIK-877',
      'backend/Services/TrainingPlans/TrainingPlanWorkoutMutationService.cs',
      'mobile/app/(modals)/past-workout-details.tsx'
    ]
  },
  {
    id: 'mutation.no-completed-edit',
    title: 'Completed planned workouts cannot be modified through mutation endpoints.',
    summary: 'Once a planned workout is completed, target edits, moves, and deletes are blocked.',
    details: [
      'UpdatePlannedWorkoutTargets rejects completed workouts.',
      'MovePlannedWorkout rejects completed workouts.',
      'DeletePlannedWorkout rejects completed workouts.',
      'AddPlannedWorkout is unaffected because it creates a new incomplete workout.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWorkoutMutationService.cs:154-157', 'backend/Services/TrainingPlans/TrainingPlanWorkoutMutationService.cs:237-240', 'backend/Services/TrainingPlans/TrainingPlanWorkoutMutationService.cs:292-295']
  },
  {
    id: 'mutation.move-same-week',
    title: 'Planned workout moves must stay within the same week.',
    summary: 'Dragging a planned workout cannot move it across training week boundaries.',
    details: [
      'MovePlannedWorkout parses target date as yyyy-MM-dd with no time component.',
      'Target date must be between the resolved source week start and end dates.',
      'Moving to the same calendar date returns success without mutating the plan.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWorkoutMutationService.cs:211-248']
  },
  {
    id: 'manual.plan-provision',
    title: 'Users without an active plan get an open-ended manual plan.',
    summary: 'Mutation context auto-provisions or reactivates a manual plan when no active plan exists.',
    details: [
      'Manual plans use goal id manual and are not tied to a hike target.',
      'Manual plan algorithm version is manual-1.0.',
      'Plan start is the Monday of the current week.',
      'Plan end is initially 10 years out, then updates as weeks are added.',
      'Current week and following week are seeded.',
      'Manual weeks are Base phase and created on demand when adding workouts outside existing weeks.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanWorkoutMutationService.cs:330-385', 'backend/Services/TrainingPlans/ManualPlanFactory.cs:10-101']
  },
  {
    id: 'regeneration.no-manual',
    title: 'Manual plans cannot be regenerated from training preferences.',
    summary: 'Regeneration requires a goal-hike plan.',
    details: [
      'If the active plan IsManual is true or GoalId is manual, regeneration returns a validation error.',
      'The user-facing error asks the user to choose a goal hike before updating training preferences.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanRegenerationService.cs:74-78']
  },
  {
    id: 'regeneration.blackout-merge',
    title: 'Regeneration merges future blackout periods.',
    summary: 'Schedule-around-dates regeneration preserves active blackout periods and adds requested ones.',
    details: [
      'Existing and requested blackout periods are normalized and combined.',
      'Blackout periods whose end date is before the regeneration as-of date are dropped.',
      'Exact duplicate start/end pairs are de-duplicated.',
      'Merged periods are sorted by start date.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanRegenerationService.cs:89-99', 'backend/Services/TrainingPlans/TrainingPlanRegenerationService.cs:144-170']
  },
  {
    id: 'regeneration.merge-completions',
    title: 'Regeneration preserves completed prescriptions and imports.',
    summary: 'Regeneration retains historical actuals without collapsing multiple same-date workouts or turning imports into goals.',
    details: [
      'Completed prescribed workouts are grouped by calendar date and can replace generated prescriptions on matching dates.',
      'Unmatched completed prescriptions and all recorded imports are appended as separate completed workouts in the regenerated week.',
      'Completed state, workout-record linkage, actuals, perceived difficulty, pain/fatigue, duration, notes, exercises, and provenance are copied.',
      'Targets are recomputed from prescribed workouts only, then every final regenerated week becomes its active Goal baseline.',
      'Coach notes whose reason is not a plan-creation reason are appended to the new plan.',
      'HSI is recalculated after regeneration and readiness context is reapplied to weekly notes.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanRegenerationService.cs', 'backend/Services/TrainingPlans/TrainingPlanRegenerationUtilities.cs']
  }
]

const CODIFIED_PLAN_RULE_GROUPS: CodifiedPlanRuleGroup[] = [
  {
    id: 'goal-inputs',
    title: 'Goal And Input Rules',
    description: 'Rules that normalize goal, user, exercise, equipment, and source data before weekly math starts.',
    rules: GOAL_INPUT_RULES
  },
  {
    id: 'timeline-targets',
    title: 'Timeline And Target Rules',
    description: 'Rules that determine plan length, phases, and total target load.',
    rules: TIMELINE_TARGET_RULES
  },
  {
    id: 'weekly-generation',
    title: 'Weekly Generation Rules',
    description: 'Rules that shape weekly targets, load progression, exercise choice, and rounding.',
    rules: WEEKLY_GENERATION_RULES
  },
  {
    id: 'structure-support',
    title: 'Structure And Support Rules',
    description: 'Rules behind v3.4 structure remediation, support placement, and final independent workout cards.',
    rules: STRUCTURE_SUPPORT_RULES
  },
  {
    id: 'milestones',
    title: 'Milestone Rules',
    description: 'Rules that replace, split, protect, and rebalance milestone work.',
    rules: MILESTONE_RULES
  },
  {
    id: 'adaptation-mutation',
    title: 'Adaptation And Mutation Rules',
    description: 'Rules that can change an existing plan after initial generation.',
    rules: ADAPTATION_MUTATION_RULES
  }
]

type PlanGenerationChangelogEntry = {
  date: string
  title: string
  summary: string
  details: string[]
  sources: string[]
}

export const PLAN_GENERATION_CHANGELOG: PlanGenerationChangelogEntry[] = [
  {
    date: '2026-10-22',
    title: 'Goal hike dates can change without restarting onboarding.',
    summary: 'HIK-1008: Planner can rebuild the active goal for a new departure date while preserving completed workouts.',
    details: [
      'The new date action opens the hike-date calendar, submits only the edited date, and returns to the refreshed Planner after success.',
      'The active goal, original training start date, pack target and completed workout history remain in use.',
      'Rebuilds evaluate proposed preferences in memory. Saved preferences and the active-plan reference change together only after the replacement plan is ready; failed rebuilds retain the previous date and plan.'
    ],
    sources: [
      'mobile/app/onboarding/hike-date.tsx',
      'backend/Services/TrainingPlans/TrainingPlanRegenerationService.cs',
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs',
      'Codified rule id: regeneration.completed-workout-carry-forward'
    ]
  },
  {
    date: '2026-10-21',
    title: 'Popular hikes can be marked as long itineraries.',
    summary: 'HIK-1080: thru-hikes use the LongItinerary profile, with whole-trip totals beside the itemized first days.',
    details: [
      'LongItinerary is available on popular hikes only. Tour-operator hikes keep the Standard and DayHike profiles.',
      'Plan targets are built from the itemized itinerary days, the same as Standard. Trip days, total trip distance, and total trip elevation gain describe the whole trip.',
      'Base Camp requires trip days greater than the itemized days, and trip totals at least as large as the itinerary sums.',
      'In the app, trip days after the itemized ones show the remaining trip distance and gain averaged per day. Modify is hidden on mobile for these hikes in v1.'
    ],
    sources: [
      'HIK-1080',
      'HikeStrong.Db/Models/PlanGenerationProfile.cs',
      'backend/DTOs/PopularHikes/PopularHikeUpsertDto.cs',
      'dashboard/src/pages/PopularHikesAdminPage.tsx',
      'Codified rule id: target.phase-clamp'
    ]
  },
  {
    date: '2026-10-21',
    title: 'General Hiking Fitness offers five hike-linked levels.',
    summary: 'Couch to Trail, Easy, Moderate, Hard, and Strenuous are available in the app and editable in Fitness Proxy Hikes.',
    details: [
      'The five default hikes are Yosemite Valley Floor Loop, Appalachian Trail: Shenandoah Shortened Central Section, Otter Trail, W Trek: Torres del Paine, and Tour du Mont Blanc: Classic Circuit.',
      'Each choice uses its linked hike itinerary and the admin-configured metric pack weight with the regular hiking-plan generator.',
      'Visible level labels use dedicated, admin-configurable string IDs, so String Tables can rename a level without another mobile release.',
      'Existing saved settings are changed deliberately through admin or the HIK-1087 configuration script; loading defaults does not overwrite admin edits. Existing generated plans are not rebuilt.'
    ],
    sources: [
      'HIK-1087',
      'backend/Models/GeneralFitnessHikeDefaults.cs',
      'backend/Controllers/FitnessHikeTargetsController.cs',
      'backend/Services/TrainingPlans/GoalHikeResolver.cs',
      'mobile/app/onboarding/choose-goal.tsx',
      'dashboard/src/pages/FitnessHikeTargetsPage.tsx',
      'scripts/configure-general-fitness-targets.mjs',
      'Codified rule id: goal.resolve.type'
    ]
  },
  {
    date: '2026-10-20',
    title: 'Elevation milestone lead-in protection opens more training days.',
    summary: 'HIK-1126: the first two standard elevation challenges protect two days before; the last two have no pre-milestone window. All four keep one day after.',
    details: [
      'The four elevation challenges use milestone IDs 1, 3, 5, and 7 because distance challenges sit between them. IDs 1 and 3 protect -2 and -1 days; IDs 5 and 7 protect no days before.',
      'Distance milestones still create no protected dates. Other elevation and user-choice milestone windows, milestone-day handling, protected-workout restrictions, and recovery rules are unchanged.',
      'The shared protected-date rule also governs later cleanup, support stacking, and durability repayment. Split or omitted milestones do not renumber protection windows.',
      'Algorithm version 6.4.30 applies when plans are generated or regenerated; this change does not rewrite saved plans.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestoneWindowRules.cs', 'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs', 'Codified rule ids: milestone.window.before, milestone.window.after-elevation']
  },
  {
    date: '2026-10-13',
    title: 'Elevation takes priority over optional mileage.',
    summary: 'Hill-repeat normalization no longer creates replacement mileage. Weekly allocation protects climbing before distance-only prescriptions.',
    details: ['The intentional L1–L4 multipliers, progression, short-plan milestone rules, recovery windows, and 30-mile maximum are unchanged.', 'With TTL disabled, final treadmill synchronization may reduce eligible ordinary distance-only sessions in the same week to deliver an existing climbing prescription. No load moves between weeks.', 'Imperial regressions check elevation priority, unchanged neighboring sessions after hill normalization, protected donors, and final shortfall accounting. Distance totals remain visible in audits but higher mileage alone is not a success criterion.'],
    sources: ['backend/Services/TrainingPlans/TrainingPlanAssignmentRounding.cs', 'backend/Services/TrainingPlans/TrainingPlanExerciseTargetSynchronizer.cs', 'backend.Tests/Unit/Services/TrainingPlanFinalCapacityTests.cs']
  },
  {
    date: '2026-10-12',
    title: 'Whole-mile ceilings and hill-repeat mileage preserve training distance.',
    summary: 'The historical rounded ceiling permits 30 full miles. This change also introduced hill-repeat mileage recovery, superseded by the October 13 elevation-first policy.',
    details: ['Recovery windows and the 30-mile maximum remain unchanged. Lower configured ceilings are preserved.', 'Recovery stays within the same week and its original total, uses whole display units, and respects treadmill limits. It does not add training days or modify milestones. Any remaining loss is traced.'],
    sources: ['backend/Services/TrainingPlans/TrainingPlanDistanceCeiling.cs', 'backend/Services/TrainingPlans/TrainingPlanAssignmentRounding.cs', 'backend.Tests/Unit/Services/TrainingPlanFinalCapacityTests.cs']
  },
  {
    date: '2026-10-12',
    title: 'Short-plan milestones retain their goal-based challenge.',
    summary: 'Short day-hike milestones no longer shrink to the original host-week budget or ordinary elevation cap.',
    details: ['The 75% target remains subject to achievable grade and the 30-mile weekly maximum. Recovery windows and unavailable-week handling are unchanged.', 'Final treadmill capacity recovery now includes short plans with TTL disabled. Imperial regressions cover milestone targets above ordinary caps and final distance/elevation accounting.'],
    sources: ['backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs', 'backend.Tests/Unit/Services/TrainingPlanMilestonePlannerTests.cs']
  },
  {
    date: '2026-10-12',
    title: 'Final cleanup preserves achievable climbing and milestone distance.',
    summary: 'Weekly distance rounding protects milestones and uses spare whole units; final treadmill checks preserve assigned climbing when existing distance capacity can carry it.',
    details: [
      'Weekly distance allocation keeps milestone targets when they fit alongside minimum ordinary sessions, then shares the remaining whole miles or kilometers without exceeding the ceiling or increasing original prescriptions.',
      'With TTL disabled, generation can add treadmill distance on the same session to carry already assigned climbing within the weekly and treadmill limits. Protected recovery dates and completion-time adjustments are excluded.',
      'Unachievable climb is rounded down within the incline limit. Final cleanup rechecks milestone and durability repayment ledgers, so removed repayments appear as unpaid debt.',
      'The existing host-week milestone budget restriction remains limited to dated, single-day DayHike goals with 28–48 days available. Standard-profile and multi-day goals remain outside that restriction.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanAssignmentRounding.cs', 'backend/Services/TrainingPlans/TrainingPlanExerciseTargetSynchronizer.cs', 'backend/Services/TrainingPlans/TrainingPlanGenerator.cs', 'backend/Services/TrainingPlans/ShortWindowDayHikePolicy.cs']
  },
  {
    date: '2026-10-12',
    title: 'Code defaults match the active production settings.',
    summary: 'New and missing settings use the production configuration captured on October 12: a 30-mile weekly ceiling, about 10 miles per treadmill workout, 5,000 ft ordinary elevation caps, and four training days.',
    details: [
      'Distance/elevation multipliers are L1 4.1/3.0, L2 4.8/3.5, L3 3.5/4.3, and L4 4.1/5.0. The separately configurable protective elevation cap also defaults to 5,000 ft.',
      'Persisted model defaults, API defaults, workout fallbacks, and initial config history notes share the production baseline. Existing saved configurations remain authoritative.',
      'The legacy weekly elevation field matches production but remains unused as a generation cap. This alignment does not remove milestone restrictions or resolve the remaining volume-shortfall audit findings.',
      'The regular backend suite now includes captured audit scenarios with Imperial profiles and reports in miles and feet; stored measurements remain metric.'
    ],
    sources: ['backend/Models/TrainingPlans/PlanGenerationDefaults.cs', 'backend.Tests/Fixtures/PlanGeneration/production-config-2026-10-12.json', 'backend.Tests/Unit/Services/TrainingPlanGeneratorImperialAuditTests.cs']
  },
  {
    date: '2026-10-12',
    title: 'Short plans start partway through the 12-week schedule.',
    summary: 'Nine-week plans retain weeks 4–12 instead of compressing twelve weeks of volume into nine.',
    details: [
      'Weekly distance, elevation, phases, and pack progression use the corresponding canonical weeks. Skipped volume is removed from plan targets and debt accounting.',
      'Calendar proration, configured distance ceilings, the 30-mile absolute maximum, optional TTL controls, and workout and milestone safety rules remain in place.',
      'Missed-workout redistribution is unchanged. This change applies to newly generated or regenerated plans; it does not rewrite saved plans.'
    ],
    sources: ['backend/Services/TrainingPlans/TrainingPlanTimelineBuilder.cs', 'backend/Services/TrainingPlans/TrainingPlanWeekTargetBuilder.cs', 'backend/Services/TrainingPlans/TrainingPlanGenerator.cs', 'Codified rule id: timeline.shortened-plan']
  },
  {
    date: '2026-09-24',
    title: 'Balance and lunge exercises advance after deliberate Too Easy feedback.',
    summary: 'HIK-1040 holds the current support exercise on Just Right and requires two consecutive Too Easy ratings to advance.',
    details: [
      'New plans keep their initial lunge window and balance tracks until completed feedback earns a step; elapsed weeks alone do not choose harder exercises.',
      'Each lunge category pair and each balance track count consecutive Too Easy ratings. Just Right breaks the pair without discarding an already earned level.',
      'Two Too Hard lunge ratings or one Too Hard balance track rating still step back. Generated support levels retain their earned position in the final two weeks.',
      'Completing a support workout rewrites incomplete future exercises for that category. Existing completed workouts stay intact, and regeneration anchors lunge progression to recognized completed exercise codes.'
    ],
    sources: ['HIK-1040', 'backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs', 'backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs', 'backend/Services/TrainingPlans/TrainingPlanLungeWorkoutUtilities.cs', 'Codified rule ids: support.rating-adjustment, support.balance-tracks, support.lunge-sequence, completion.lunge-rewrite']
  },
  {
    date: '2026-09-08',
    title: 'Combined climbing workouts share their elevation target.',
    summary: 'HIK-1026 prevents final target synchronization from leaving supplemental step-ups with zero elevation before independent workout splitting.',
    details: [
      'Unassigned elevation is shared across the selected climbing exercises; existing positive proportions are retained when targets change.',
      'A 995 ft stair-machine and step-up workout becomes approximately 500 ft and 495 ft, preserving the exact stored metric total.',
      'Distance carriers and support exercises do not receive part of the climbing target. Treadmill incline limits still apply.',
      'Completed and imported workouts remain unchanged. Existing saved plans require regeneration to use the corrected generation behavior.'
    ],
    sources: ['HIK-1026', 'backend/Services/TrainingPlans/TrainingPlanExerciseTargetSynchronizer.cs', 'backend/Services/TrainingPlans/TrainingPlanGeneratedWorkoutSplitter.cs', 'Codified rule id: exercise.target-sync.final']
  },
  {
    date: '2026-09-08',
    title: 'Missed workouts prompt a schedule review instead of mileage debt.',
    summary: 'HIK-1018 removes automatic catch-up increases and enforces a shared 30-mile maximum, retaining lower configured limits.',
    details: [
      'Low and high HSI no longer increase future prescriptions at workout completion; recovery reductions remain available.',
      'Two distinct missed primary training days in the preceding 14 local days offer an optional Planner link to plan settings. Today is excluded.',
      'Generation and post-completion safeguards share the effective distance ceiling. Completed and past workout history is preserved.',
      'Manual edits cannot add distance beyond the limit, but can reduce an already oversized week.',
      'No production plan is regenerated or repaired by this code change alone.'
    ],
    sources: ['HIK-1018', 'backend/Services/TrainingPlans/TrainingPlanAdaptationUtilities.cs', 'backend/Services/TrainingPlans/TrainingPlanDistanceCeiling.cs', 'backend/Services/TrainingPlans/TrainingPlanScheduleCheckIn.cs', 'mobile/components/planner/MyPlanTab.tsx', 'Codified rule ids: adaptation.no-catch-up, adaptation.schedule-check-in, adaptation.apply-caps, weekly.ceiling.post-round.distance']
  },
  {
    date: '2026-09-04',
    title: 'Short day-hike plans add bounded milestones and explain preparation limits.',
    summary: 'HIK-976 adds a dedicated milestone policy for dated single-day DayHike goals with four to six weeks available.',
    details: [
      'Four-week plans aim for a 75% elevation milestone two weeks before the hike; plans with five or six weeks also aim for a 75% distance milestone three weeks before.',
      'Existing weekly workload, ordinary elevation limits, grade constraints, and measurement rounding can reduce the targets. Unavailable milestone weeks are skipped.',
      'A persistent planner notice explains that preparation and milestone completion do not confirm hike readiness.',
      'Existing compressed periodization and progression rules remain shared with the standard generator.'
    ],
    sources: [
      'HIK-976',
      'backend/Services/TrainingPlans/ShortWindowDayHikePolicy.cs',
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs',
      'mobile/components/planner/MyPlanTab.tsx',
      'Codified rule id: plan.short-window-day-hike'
    ]
  },
  {
    date: '2026-09-04',
    title: 'Elevation exercise targets preserve fractional meters.',
    summary: 'HIK-990 keeps a 500 ft stair-machine target at 50 floors by preserving metric precision through assignment and final synchronization.',
    details: [
      'Exercise assignments retain the workout total and fractional remainder instead of rounding the total to whole meters.',
      'Exact feet-to-meters conversion keeps clean imperial targets from advancing to the next increment on normalization.',
      'Final synchronization detects sub-meter target differences and copies the authoritative elevation without whole-meter rounding.',
      'Stair-machine tracking and saving retain calculated meters while rounding only the visible elevation.'
    ],
    sources: [
      'HIK-990',
      'backend/Services/TrainingPlans/TrainingPlanAssignmentRounding.cs',
      'backend/Services/TrainingPlans/TrainingPlanExerciseTargetSynchronizer.cs',
      'mobile/lib/workoutTracking/exerciseProfiles.ts',
      'mobile/app/(modals)/save-workout.tsx',
      'Codified rule ids: rounding.elevation, exercise.target-sync.final'
    ]
  },
  {
    date: '2026-09-04',
    title: 'Plan rebuilds preserve the selected goal and calendar after switching hike types.',
    summary: 'HIK-998 fixes stale onboarding dates and pack settings replacing an active beginner program during preference, Coach or admin rebuilds.',
    details: [
      'Rebuilds preserve goal identity, proxy label/level, target date, pack target and calendar boundaries unless the request explicitly changes a target.',
      'Undated custom-duration plans retain their duration and partial first week when training days change.',
      'Selecting an unknown hike date clears the previous date; generation also ignores stale dates in older records marked unknown.',
      'Explicit empty equipment, elevation-access and concern lists clear selections while omitted fields remain unchanged.',
      'Same-account API sequences cover real-to-beginner-to-real switches, repeated rebuilds, time away, completions, explicit target edits and partial updates.'
    ],
    sources: [
      'HIK-998',
      'backend/Services/TrainingPlans/TrainingPlanRegenerationUtilities.cs',
      'backend/Services/TrainingPlans/TrainingPlanTimelineBuilder.cs',
      'backend/Services/UserService.cs',
      'backend.Tests/Integration/Controllers/TrainingPlanLifecycleIntegrationTests.cs',
      'Codified rule id: regeneration.completed-workout-carry-forward'
    ]
  },
  {
    date: '2026-08-19',
    title: 'Milestone Coach Notes now describe the aggregate hardest-day percentage.',
    summary: 'HIK-957 removes unsupported ordinal itinerary-day matches from milestone notes while preserving the existing milestone target calculations.',
    details: [
      'Elevation comparisons sum all same-day split components before comparing the milestone with the hardest climbing day.',
      'Distance comparisons use the final milestone distance against the longest itinerary day.',
      'The displayed percentage comes from final generated targets, so component rounding and hard distance clamps remain represented accurately.',
      'Coach-note rationale now records the target percentage and metric instead of a fabricated simulated itinerary day and rank.'
    ],
    sources: [
      'HIK-957',
      'backend/Services/TrainingPlans/TrainingPlanWeeklyCoachNotePlanner.cs',
      'backend.Tests/Unit/Services/TrainingPlanWeeklyCoachNotePlannerTests.cs',
      'Codified rule ids: milestone.distance-focus, milestone.elevation-focus'
    ]
  },
  {
    date: '2026-08-19',
    title: 'Automatic plan adjustments keep workout and exercise targets synchronized.',
    summary: 'HIK-935 makes workout totals authoritative after survey/HSI changes and gives Coach a deterministic date-range schedule summary instead of asking the LLM to add embedded exercise values.',
    details: [
      'Post-completion survey throttles, HSI adaptations, and balance-progression updates now run the final exercise-target synchronizer before persistence.',
      'The equality check now repairs visible target differences such as 51 m at workout level versus a stale 47 m exercise prescription.',
      'Coach can request an inclusive local date range up to 93 days and receives server-computed scheduled, completed-actual, remaining, and recorded-extra totals in the user measurement system.',
      'Coach prompt context suppresses conflicting exercise-level distance/elevation values, while exact quantity questions are required to use the deterministic tool.'
    ],
    sources: [
      'HIK-935',
      'backend/Services/TrainingPlans/TrainingPlanWorkoutCompletionService.cs',
      'backend/Services/TrainingPlans/TrainingPlanExerciseTargetSynchronizer.cs',
      'backend/Services/CoachToolsService.cs',
      'backend/Services/TrainingPlanPromptContext.cs',
      'Codified rule id: exercise.target-sync.final'
    ]
  },
  {
    date: '2026-08-18',
    title: 'Workout grade caps keep prescribed steepness physically achievable.',
    summary: 'Per-fitness-band grade caps and a 12% treadmill incline cap stop prescriptions like a 2-mile / 3,000 ft day; excess elevation spreads across the week or adds distance instead of being dropped.',
    details: [
      'Every workout is capped at a configurable max elevation per km by fitness band (105/130/160/190 m/km for levels 1-4).',
      'Treadmill-only elevation days are additionally capped at treadmill distance times the configured max incline (default 12%).',
      'Too-steep days first spread excess elevation to same-week days with grade headroom, then add distance within the hard weekly distance ceiling, and only trim elevation as a traced last resort.',
      'Elevation milestones are never elevation-reduced: they and their split components now carry enough distance to keep the climb within the grade cap, and milestones waive the treadmill distance cap so they stay single-exercise workouts.',
      'Both caps and the milestone carry distance emit workout.grade-cap / treadmill.incline-cap trace entries, and both settings are editable on the plan-algorithm config form.'
    ],
    sources: [
      'backend/Models/TrainingPlans/PlanGenerationConfig.cs',
      'backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs',
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs',
      'backend/Services/TrainingPlans/TrainingPlanExerciseTargetSynchronizer.cs',
      'Codified rule ids: workout.grade-cap, treadmill.incline-cap, milestone.elevation-focus, exercise.target-sync.final'
    ]
  },
  {
    date: '2026-08-17',
    title: 'Distance milestones now preserve training days and balance displaced elevation.',
    summary: 'HIK-946 keeps the phase/TTL day count, water-fills removed milestone elevation across usable workouts, and restores final elevation-modality variety.',
    details: [
      'Workout-date variety scoring no longer silently removes a resolved training day when the same preferred weekdays repeat.',
      'When a distance milestone removes elevation from its host, the displaced load raises the least-loaded eligible same-week workouts toward balance instead of being consumed wholesale by the first elevation workout.',
      'Final reconciliation restores the configured true-elevation minimum after milestone and cleanup mutations.',
      'When both are accessible, artificial elevation workouts alternate treadmill and stair machine at week level while preserving support exercises and workout targets.',
      'Successful redistribution and final modality changes now include paired before/after trace contexts.'
    ],
    sources: [
      'HIK-946',
      'backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs',
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs',
      'backend/Services/TrainingPlans/TrainingPlanElevationModalityReconciler.cs',
      'Codified rule ids: workout.date-selection, milestone.redistribute-week, exercise.elevation-modality.final'
    ]
  },
  {
    date: '2026-08-10',
    title: 'HSI now waits for five eligible completions in the active plan.',
    summary: 'Imported activity and zero-target support work no longer unlock a numeric readiness score before the user has enough prescribed HSI history.',
    details: [
      'The five-workout threshold now counts only completed active-plan prescriptions with a distance, elevation, or pack target.',
      'Recorded workout imports remain part of actual progress and HSI actual load, but they do not satisfy the readiness sample threshold.',
      'Completed balance, lunge, or other support cards with no HSI target also do not satisfy the threshold.',
      'The mobile HSI card and backend weekly coach-note readiness copy use matching eligibility rules.',
      'Fresh plans therefore keep the numeric HSI hidden until the new plan has five eligible completions; ordinary same-plan regeneration can retain eligible carried completions.'
    ],
    sources: [
      'HIK-904',
      'backend/Models/TrainingPlans/GeneratedTrainingPlan.cs',
      'backend/Services/TrainingPlans/TrainingPlanWeeklyCoachNotePlanner.cs',
      'mobile/lib/hsi.ts',
      'Codified rule id: hsi.coach-notes-minimum-workouts'
    ]
  },
  {
    date: '2026-08-09',
    title: 'Training preference changes keep the active plan week.',
    summary: 'Regeneration now reuses the active plan timeline instead of restarting the replacement at week 1.',
    details: [
      'Removing or adding a workout or equipment preference still regenerates the plan so future prescriptions match the user’s available training options.',
      'The replacement plan uses the active plan’s original start date, so a user who changes preferences in week 4 remains in week 4.',
      'Completed prescriptions and recorded workout imports retain their dates, actuals, record links, exercises, notes, and provenance through the existing carry-forward rule.',
      'The goal date and future plan horizon remain intact; only the regenerated schedule reflects the updated preferences.',
      'The current save date still decides which blackout periods have fully passed and may be discarded.'
    ],
    sources: [
      'HIK-902',
      'backend/Services/TrainingPlans/TrainingPlanRegenerationService.cs',
      'backend/Services/TrainingPlans/TrainingPlanRegenerationUtilities.cs',
      'Codified rule id: regeneration.completed-workout-carry-forward'
    ]
  },
  {
    date: '2026-08-07',
    title: 'Curated day hikes can train below the standard weekly floors.',
    summary: 'Tour-operator hikes and popular hikes now select a Standard or DayHike plan-generation profile.',
    details: [
      'Existing and newly created hikes default to Standard, preserving the current plan-generation behavior.',
      'Base Camp exposes the profile on each tour-operator hike and popular hike so specific short hikes can be marked Day hike.',
      'DayHike sets the configured 16.0 km distance floor and 300 m elevation floor to 0 at both weekly-target creation and the later Base/Peak phase clamp.',
      'The 0 floors allow itinerary-derived weekly targets below the Standard minimums; they do not force either target to 0.',
      'The hard weekly distance ceiling, durability behavior, milestones, workout structure, and separate PreBase cohort-minimum rule are unchanged.',
      'The selected profile is resolved from the curated goal and recorded in the plan-generation trace when DayHike relaxation is active.'
    ],
    sources: [
      'HIK-899',
      'HikeStrong.Db/Models/PlanGenerationProfile.cs',
      'backend/Services/TrainingPlans/GoalHikeResolver.cs',
      'backend/Services/TrainingPlans/TrainingPlanWeekTargetBuilder.cs',
      'backend/Services/TrainingPlans/TrainingPlanTargetPolicyPlanner.cs',
      'dashboard/src/pages/TourManagementPage.tsx',
      'dashboard/src/pages/PopularHikesAdminPage.tsx',
      'Codified rule id: target.phase-clamp'
    ]
  },
  {
    date: '2026-08-06',
    title: 'Hiking and endurance now resolve through four explicit fitness bands.',
    summary: 'Plan volume no longer uses a reversed five-level scale or a combined Level 1–2 multiplier row.',
    details: [
      'Level 1 consistently means least active and Level 4 means most active across onboarding answers, backend target math, Plan Tester controls, and Plan Algorithm settings.',
      'Plan Tester option labels mirror the mobile English choices and display their resolved L1–L4 band.',
      'Occasionally and NotAtAll endurance answers share Level 1; FewTimes and NotAtAll hiking answers also share Level 1.',
      'The lower of the endurance and hiking bands selects the row, so the less-active answer still governs both distance and elevation.',
      'Each of Levels 1, 2, 3, and 4 has its own distance and elevation multiplier fields; legacy Mongo snapshots retain their four saved multiplier pairs.'
    ],
    sources: [
      'backend/Services/TrainingPlans/TrainingPlanTargetPolicyPlanner.cs',
      'backend/Models/TrainingPlans/PlanGenerationConfig.cs',
      'dashboard/src/components/plan-algorithm/PlanConfigFormFields.tsx',
      'dashboard/src/pages/TrainingPlanTestPage.tsx',
      'Codified rule id: target.difficulty-level',
      'Codified rule id: target.difficulty-multipliers'
    ]
  },
  {
    date: '2026-08-06',
    title: 'Base Camp frequency edits now regenerate targets with explicit tour units.',
    summary: 'Changing a user’s hiking or endurance frequency from Base Camp now rebuilds generated plans, while tour highest points retain whether their unitless values are metric or imperial.',
    details: [
      'Generated plans prompt for confirmation, preserve completed workout history through the shared regeneration service, and reload the user, plan, and current week after success.',
      'Manual plans remain manual and only save the profile change.',
      'Tour create/edit forms display and persist the selected Metric or Imperial measurement system for itinerary, pack-weight, and highest-point values.',
      'Goal resolution treats explicit unit suffixes as authoritative, then uses the persisted tour measurement system for unitless highest points and pack weights.'
    ],
    sources: [
      'HIK-890',
      'backend/Controllers/AdminMobileUsersController.cs',
      'backend/Services/TrainingPlans/TrainingPlanRegenerationUtilities.cs',
      'backend/Services/TrainingPlans/GoalHikeResolver.cs',
      'dashboard/src/pages/MobileUserDetailPage.tsx',
      'dashboard/src/pages/TourManagementPage.tsx',
      'Codified rule id: target.difficulty-level'
    ]
  },
  {
    date: '2026-08-05',
    title: 'Exercise access is now enforced through final plan generation.',
    summary: 'Late repair and target-synchronization passes can no longer reintroduce hikes, stairs, machines, or other gated exercises that the user did not select.',
    details: [
      'The onboarding-derived accessible exercise catalog now flows through normal assembly, milestones, support repair, semantic cleanup, final target synchronization, and workout-card splitting.',
      'Distance carriers added to stairs or step-ups must come from the accessible catalog, so users without local hike or trail access receive Run/Walk rather than Hike.',
      'A final runtime invariant rejects generation if any gated exercise remains after all repair and split passes.',
      'Representative plan tests now verify every prescribed exercise against each scenario user’s selected access.'
    ],
    sources: [
      'HIK-885',
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs',
      'backend/Services/TrainingPlans/TrainingPlanUserEquipmentAccess.cs',
      'backend.Tests/Unit/Services/TrainingPlanGeneratorTests.cs',
      'Codified rule id: equipment.access-filter'
    ]
  },
  {
    date: '2026-08-04',
    title: 'Plan generation now uses active Base Camp exercise codes exclusively.',
    summary: 'Unmatched historical exercise aliases and nonexistent test fixtures were removed so generation, repair, and target synchronization describe the staging catalog directly.',
    details: [
      'Distance and support-host selection now recognizes only the active hike, run, stair, hill, treadmill, stair-machine, and step-up definitions.',
      'HIIT fallback classification now uses the active rower, rowing-machine, and stationary-bike codes instead of obsolete modality names.',
      'Backend and mobile fixtures now use real Base Camp definitions rather than invented trail, strength, recovery, or equipment variants.',
      'Apple Watch and HealthKit integration uses the canonical stair-climb code end-to-end; HealthKit still receives its native stair-climbing activity type.'
    ],
    sources: [
      'HIK-872',
      'backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs',
      'backend/Services/TrainingPlans/TrainingPlanExerciseTargetSynchronizer.cs',
      'backend/Services/TrainingPlans/TrainingPlanMutationUtilities.cs',
      'mobile/lib/healthBridge/watchWorkoutSync.ts',
      'Codified rule ids: exercise.active-only, equipment.access-filter, support.stack-target'
    ]
  },
  {
    date: '2026-08-04',
    title: 'Supplemental Step Ups now remain elevation-target workouts.',
    summary: 'Artificial-elevation plans can still add canonical STEP_UPS above the threshold, but the resulting card no longer inherits a legacy strength sets-and-reps prescription.',
    details: [
      'The supplemental rule continues to require more than 200 m of elevation, Step Ups access, and no knee concern.',
      'STEP_UPS receives its rounded share of the elevation target and preserves any assigned pack weight when split into an independent card.',
      'Obsolete strength-style Step Ups test definitions were removed because they do not exist in the active Base Camp exercise catalog.',
      'Mobile planner cards now have focused proof that elevation-based Step Ups omit sets and reps.'
    ],
    sources: [
      'HIK-872',
      'backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs',
      'backend/Services/TrainingPlans/TrainingPlanGeneratedWorkoutSplitter.cs',
      'mobile/lib/useTrainingPlan.ts',
      'Codified rule id: exercise.stepups-threshold'
    ]
  },
  {
    date: '2026-08-04',
    title: 'Balance support now prescribes two complete rounds.',
    summary: 'Balance workouts keep all three knee, hip, and tandem exercises while reducing the default session from three rounds to two.',
    details: [
      'The exercise-track selection and progression order are unchanged.',
      'Generated exercise notes now prescribe 2 sets with 1 minute of work and 15 seconds of rest.',
      'The guided mobile timer repeats the full three-exercise sequence exactly twice.',
      'Starting and advanced balance-track windows both retain one exercise from each track.'
    ],
    sources: [
      'HIK-852',
      'backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs',
      'mobile/lib/balanceWorkout.ts',
      'Codified rule id: support.balance-tracks'
    ]
  },
  {
    date: '2026-08-04',
    title: 'Lunge support now prescribes two exercises per workout.',
    summary: 'New lunge workouts begin with Reverse Lunge and Walking Lunge while Reverse Slide Lunge remains the next progression step.',
    details: [
      'The fixed lunge progression window shrank from three consecutive exercises to two.',
      'The initial BWT2/BWT3 window excludes Reverse Slide Lunge so the workout is shorter.',
      'Advancing the progression moves to BWT3/BWT4, preserving Reverse Slide Lunge as later work.',
      'The mobile planner recognizes and routes both initial and advanced two-exercise prescriptions to the guided lunge workout.'
    ],
    sources: [
      'HIK-851',
      'backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs',
      'mobile/lib/lungeWorkout.ts',
      'Codified rule id: support.lunge-sequence'
    ]
  },
  {
    date: '2026-08-04',
    title: 'Uniform HSI plan redistribution is disabled by default.',
    summary: 'New HSI policies keep readiness scoring active without automatically rewriting future training weeks after workout completion.',
    details: [
      'EnableUniformRedistribution now defaults to false in persisted model and API DTO defaults.',
      'The disabled gate prevents too-hard remaining-plan reduction, underperforming-HSI redistribution, and positive high-HSI adaptations.',
      'Survey elevation throttling and balance-support progression remain separate behaviors.',
      'Existing active environment policies retain their persisted value and require an explicit policy update.'
    ],
    sources: [
      'HIK-790',
      'backend/Models/TrainingPlans/HikeStrongIndexConfig.cs',
      'backend/DTOs/TrainingPlans/HikeStrongIndexConfigDto.cs',
      'backend/Services/TrainingPlans/TrainingPlanWorkoutCompletionService.cs',
      'Codified rule id: adaptation.uniform-redistribution-toggle'
    ]
  },
  {
    date: '2026-07-28',
    title: 'Automatic plan adaptations now become the active weekly goal baseline.',
    summary: 'System redistribution no longer makes untouched weeks appear user-revised while the original generated prescription remains available for history.',
    details: [
      'Automatic HSI redistribution, survey throttling, too-hard reduction, positive distance/elevation/pack adaptations, and balance progression rebaseline weeks that were untouched before the system change.',
      'Weeks already revised by the user keep their prior baseline and continue to show Revised.',
      'Recorded workout imports contribute to actual progress and HSI actual load without increasing prescribed weekly targets, plan targets, HSI planned load, or mobile optimistic goals.',
      'A recorded workout may reuse an empty rest-day planner slot, but the replacement is still stamped as a WorkoutImport and excluded from the prescription.',
      'Automatic throttle, reduction, and metric-scaling passes leave completed workouts and recorded imports unchanged.',
      'Repeated completion and wearable requests do not reapply adaptations or duplicate imports.',
      'Regenerated plans preserve matched and unmatched completed prescriptions plus same-date recorded imports as distinct workouts, then establish their active baseline after all post-processing finishes.',
      'Newly persisted plans use fresh workout identities, and same-user whole-plan mutations are serialized so stale or concurrent requests do not overwrite visible state.',
      'New nullable revision-baseline fields preserve compatibility with production-era plans that only contain the original prescription.',
      'Mobile DTOs expose the active system baseline as the Goals-column values for distance, elevation, pack, weighted workouts, lunges, balance, and HIIT; admin detail and exports expose both the active baseline and immutable original prescription.'
    ],
    sources: [
      'HIK-790',
      'backend/Models/TrainingPlans/GeneratedTrainingPlan.cs',
      'backend/Services/TrainingPlans/TrainingPlanWorkoutCompletionService.cs',
      'backend/Services/TrainingPlans/TrainingPlanMutationUtilities.cs',
      'backend/Services/TrainingPlans/TrainingPlanRecordedWorkoutProjector.cs',
      'backend/Services/TrainingPlans/TrainingPlanRegenerationService.cs',
      'backend/Services/TrainingPlans/TrainingPlanMutationCoordinator.cs',
      'Codified rule ids: adaptation.survey-throttle, adaptation.too-hard-reduction, adaptation.underperform-redistribute, adaptation.advance-simulation, adaptation.specificity-shift, completion.balance-rewrite'
    ]
  },
  {
    date: '2026-07-16',
    title: 'Treadmill overflow keeps its Run/Walk distance after workout splitting.',
    summary: 'Final exercise synchronization now enforces the treadmill cap even when exercise distances already match the workout total.',
    details: [
      'A distance-only cleanup can leave a Run/Walk carrier beside a treadmill that still owns the entire workout distance.',
      'The final sync now detects that over-cap treadmill assignment, caps the treadmill, and moves the overflow onto Run/Walk.',
      'The independent-card split then preserves positive distance on both cards instead of producing a title-only Run/Walk card.',
      'Workout and weekly distance totals remain unchanged.'
    ],
    sources: [
      'HIK-736',
      'backend/Services/TrainingPlans/TrainingPlanExerciseTargetSynchronizer.cs',
      'backend/Services/TrainingPlans/TrainingPlanGeneratedWorkoutSplitter.cs',
      'Codified rule id: exercise.target-sync.final'
    ]
  },
  {
    date: '2026-07-16',
    title: 'Production HSI scoring now requires one valid active policy.',
    summary: 'Readiness and calculation fail closed instead of synthesizing believable HSI scores from missing or malformed configuration.',
    details: [
      'Production readiness is unavailable unless exactly one active HSI configuration exists and passes structural validation.',
      'Admin activation rejects incomplete legacy policies before changing the active configuration.',
      'Calculation no longer repairs non-positive epsilon, substitutes distance-only weights, or invents default band labels.',
      'Automatic default seeding and the high-severity instrumented fallback are scoped to Development and Testing.'
    ],
    sources: [
      'HIK-752',
      'backend/Services/TrainingPlans/HikeStrongIndexConfigValidator.cs',
      'backend/Services/TrainingPlans/HikeStrongIndexConfigService.cs',
      'backend/Services/TrainingPlans/HikeStrongIndexService.cs',
      'backend/Program.cs',
      'Codified rule id: hsi.score.weights'
    ]
  },
  {
    date: '2026-07-14',
    title: 'Repeated generation requests no longer replace an active plan.',
    summary: 'Stale onboarding routes and ordinary retries now return the existing active plan, while deliberate replans remain available.',
    details: [
      'The generation request carries an explicit replan signal from mobile replan flows.',
      'An ordinary retry with an active plan performs no generation or insert and returns the current plan.',
      'Overlapping initial-generation or replan requests are serialized by a five-minute crash-recoverable lease.',
      'An explicit replan still creates and activates a replacement plan.',
      'The guard covers lost responses, stale route remounts, and direct retries after login.'
    ],
    sources: [
      'HIK-723',
      'backend/Controllers/MobileTrainingPlanController.cs',
      'mobile/lib/trainingPlanGeneration.ts',
      'Codified rule id: generation.existing-plan-retry'
    ]
  },
  {
    date: '2026-07-14',
    title: 'Coach-note HSI visibility now follows the five-workout readiness gate.',
    summary: 'Weekly coach notes no longer reveal an HSI score while the mobile HSI card still says there is not enough data.',
    details: [
      'Coach-note readiness copy stays generic until five planned workouts are completed.',
      'HSI score, band, and trend rationale are removed below the threshold.',
      'Older persisted weekly notes are scrubbed when a below-threshold mobile week is returned.',
      'The existing at-five-workouts HSI behavior remains unchanged.'
    ],
    sources: [
      'HIK-732',
      'backend/Services/TrainingPlans/TrainingPlanWeeklyCoachNotePlanner.cs',
      'backend/Controllers/MobileTrainingPlanController.cs',
      'mobile/lib/hsi.ts',
      'Codified rule id: hsi.coach-notes-minimum-workouts'
    ]
  },
  {
    date: '2026-07-13',
    title: 'Balance knee progression now starts with Single Leg Pendulum Swing.',
    summary: 'New plans begin the knee balance track at BKT3 and no longer prescribe Marching or Single Leg Mummy Pose.',
    details: [
      'The generated knee track is now BKT3, BKT4, BKT5, BKT6.',
      'BKT1 and BKT2 remain in the exercise catalog so existing beta plans can still display and complete them.',
      'Completion feedback from legacy BKT1/BKT2 workouts maps into the new knee-track starting point before future workouts are rewritten.',
      'The mobile Balance Progression view and generic balance-workout starter use the same BKT3 starting point.'
    ],
    sources: [
      'HIK-707',
      'backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs',
      'mobile/app/(modals)/balance-progression.tsx',
      'mobile/app/(modals)/balance-workout.tsx',
      'Codified rule id: support.balance-tracks'
    ]
  },
  {
    date: '2026-07-02',
    title: 'Elevation milestone post-workout protection is shorter.',
    summary: 'Elevation and user-choice milestones now protect one day after the milestone instead of two, opening an extra training day on milestone weeks.',
    details: [
      'Pre-milestone windows stayed at three days in this update; the October 20, 2026 change supersedes that lead-in for the four standard elevation challenges.',
      'Only the +1 day after elevation-like milestones remains protected.',
      'Distance milestones continue to create no protected-window dates.',
      'Milestone-window cleanup still removes elevation, HIIT, and lunge stress from protected dates.'
    ],
    sources: [
      'backend/Services/TrainingPlans/TrainingPlanMilestoneWindowRules.cs',
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs',
      'Codified rule id: milestone.window.after-elevation'
    ]
  },
  {
    date: '2026-07-01',
    title: 'Hiking Fitness now uses the regular hiking planner.',
    summary: 'The old GeneralFitness-only generator path was removed; Hiking Fitness resolves to hike-shaped data and then runs through the normal plan generator.',
    details: [
      'GeneralFitness levels resolve to a linked PopularHike itinerary when configured, or to a synthesized one-day hike from configured target totals.',
      'Generated Hiking Fitness plans now use the same Base, Build, Peak, and Taper timeline, weekly target distribution, milestone insertion, workout assembly, and coach-note context as regular hiking goals.',
      'New generated plans are finite plans, no longer write cycle metadata, and no longer emit Deload as a training phase.',
      'Historical persisted Deload weeks deserialize as Build so older saved plans remain readable.'
    ],
    sources: [
      'backend/Services/TrainingPlans/GoalHikeResolver.cs',
      'backend/Services/TrainingPlans/TrainingPlanTimelineBuilder.cs',
      'backend/Services/TrainingPlans/TrainingPlanTargetPolicyPlanner.cs',
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs',
      'backend/Serialization/TrainingPhaseBsonSerializer.cs'
    ]
  },
  {
    date: '2026-07-01',
    title: 'Generated planner cards are saved as independent workouts.',
    summary: 'Multi-card generated workout bundles are split before original prescribed targets are seeded, so card totals and weekly prescribed totals stay aligned.',
    details: [
      'The final pass runs after workout totals are synchronized to exercise targets.',
      'Distance, elevation, and HIIT cards become independent same-date workouts with their own IDs and metric ownership.',
      'Lunge and balance progressions stay grouped as support workout cards, with zero distance and elevation.',
      'The target waterfall records the split stage before final generated-workout totals.'
    ],
    sources: [
      'HIK-693',
      'backend/Services/TrainingPlans/TrainingPlanGeneratedWorkoutSplitter.cs',
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs',
      'Codified rule id: workout.independent-exercise-split'
    ]
  },
  {
    date: '2026-06-28',
    title: 'Distance-only non-milestone workouts prefer Run/Walk.',
    summary: 'Distance-only training days now choose RUN before HIKE, including protected milestone-window workouts that keep distance after elevation is stripped.',
    details: [
      'True-elevation workout selection short-circuits distance-only non-milestone work to RUN/HIKE preference order.',
      'Protected-window restrictions rebuild remaining non-milestone distance with RUN before HIKE.',
      'Distance milestone prescriptions keep their existing milestone-specific behavior.'
    ],
    sources: [
      'HIK-668',
      'backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs',
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs',
      'Codified rule ids: exercise.distance-only-selection, milestone.window.restrict-workouts'
    ]
  },
  {
    date: '2026-06-28',
    title: 'Low-load support hosts are restricted during final support repair.',
    summary: 'Support repair no longer inflates the settled distance contract by creating synthetic low-load hosts during final cleanup.',
    details: [
      'Balance and lunge progression can still create a low-load distance host when the caller explicitly permits added volume and the week has safe headroom.',
      'Generated-plan final cleanup now stacks required support onto an existing safe host or skips host creation when none exists.',
      'This keeps late support repair from adding distance after weekly targets have already settled.'
    ],
    sources: [
      'Commit f15b306c',
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs:568-604',
      'backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs:442-443',
      'backend/Services/TrainingPlans/TrainingPlanExerciseSemanticSanitizer.cs:27-37',
      'Codified rule ids: support.balance.week2, support.lunge.week3, support.stack-target'
    ]
  },
  {
    date: '2026-06-28',
    title: 'HIIT placement allows adjacency to long distance, but not dedicated elevation.',
    summary: 'HIIT spacing was clarified so long-distance adjacency is allowed while dedicated-elevation adjacency remains blocked.',
    details: [
      'HIIT candidates exclude the long-distance role date and dedicated elevation role date.',
      'HIIT may be one calendar day away from the long-distance date.',
      'HIIT must be more than one calendar day away from the dedicated elevation date.'
    ],
    sources: [
      'Commit e0dd3f65',
      'backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs:47',
      'Codified rule id: workout.hiit-placement'
    ]
  },
  {
    date: '2026-06-28',
    title: 'Low-load distance hosts can preserve support work when explicitly allowed.',
    summary: 'Balance and lunge progression gained a narrow fallback that can convert an empty rest day into a low-load distance host.',
    details: [
      'The fallback applies only to balance or lunge support when no safe existing stack host remains.',
      'The week must still have rest-day, phase-cap, and selected-training-day headroom.',
      'The generated host is a short distance workout using an approved low-load run, hike, trail, or treadmill primary.'
    ],
    sources: [
      'Commit 1bd84784',
      'backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs:621-663',
      'backend/Services/TrainingPlans/TrainingPlanWorkoutAssemblyBuilder.cs:930-1048',
      'Codified rule ids: support.balance.week2, support.lunge.week3, support.stack-target'
    ]
  },
  {
    date: '2026-06-27',
    title: 'Distance milestones preserve the itinerary percentage target.',
    summary: 'Distance milestones are no longer reduced to the host workout distance or original week total.',
    details: [
      'The milestone percentage is applied to the longest itinerary day first.',
      'Distance milestones still clamp to config.WeeklyMaxDistanceKm when that hard weekly max is configured.',
      'The remaining non-milestone weekly distance is redistributed around the milestone rather than shrinking the milestone itself.'
    ],
    sources: [
      'Commit 14790998',
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:231-242',
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:1287-1300',
      'Codified rule id: milestone.clamp-week-capacity'
    ]
  },
  {
    date: '2026-06-27',
    title: 'Final exercise targets are synchronized after all cleanup.',
    summary: 'A final pass now reconciles workout-level distance and elevation totals back down to exercise prescriptions.',
    details: [
      'The pass runs after repayment, rounding, concentration cleanup, semantic cleanup, and support repair.',
      'Completed and imported workouts are skipped.',
      'The generation trace records checked workouts, corrected distance/elevation mismatches, and any remaining mismatches.'
    ],
    sources: [
      'Commit b6df21f9',
      'backend/Services/TrainingPlans/TrainingPlanExerciseTargetSynchronizer.cs:24-32',
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs:633-647',
      'Codified rule id: exercise.target-sync.final'
    ]
  },
  {
    date: '2026-06-27',
    title: 'TTL durability capping became an opt-in generation guardrail.',
    summary: 'Generated plans now default to the TTL disabled path unless a request explicitly enables the TTL durability cap.',
    details: [
      'The request DTO defaults DisableTtlDurabilityCap to true.',
      'Disabled TTL runs emit a trace and waterfall stage explaining that TTL was intentionally skipped.',
      'When TTL is disabled, no TTL debt is created because the destructive cap did not run.'
    ],
    sources: [
      'Commit b6df21f9',
      'backend/DTOs/TrainingPlans/PlanGenerationDtos.cs:98',
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs:267-275',
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs:991-1011',
      'Codified rule id: durability.cap.ttl'
    ]
  },
  {
    date: '2026-06-27',
    title: 'Target comparison exposes raw, feasible, and final workout totals.',
    summary: 'Plan-generation tester/admin responses now include target comparison and debt accounting for distance, elevation, and pack.',
    details: [
      'The comparison separates raw goal-derived targets, feasible plan targets, and final generated workout totals.',
      'It reports distance/elevation debt from raw, variance from raw and feasible, and pack deltas.',
      'Durability, milestone, and elevation-concentration debt totals are included so reductions stay explainable.'
    ],
    sources: [
      'Commit a00359bc',
      'backend/Models/TrainingPlans/PlanTargetComparison.cs',
      'backend/Services/TrainingPlans/TrainingPlanGenerator.cs:780-849',
      'backend/DTOs/TrainingPlans/PlanGenerationDtos.cs:511-521'
    ]
  },
  {
    date: '2026-06-26',
    title: 'Optional TTL reductions become durability debt instead of disappearing.',
    summary: 'When the optional TTL guardrail is enabled and removes distance or elevation, the removed target can be repaid into safe capacity.',
    details: [
      'TTL debt records removed distance and elevation as explicit plan-generation trace entries when TTL is enabled.',
      'Repayment prefers eligible non-protected, non-milestone workouts with remaining weekly and spike-guard capacity.',
      'Unpaid debt is reported instead of silently deleting target intent.'
    ],
    sources: [
      'Commit 1fa09d92',
      'backend/Services/TrainingPlans/TrainingPlanDurabilityDebtPlanner.cs:45-81',
      'backend/Services/TrainingPlans/TrainingPlanDurabilityDebtPlanner.cs:162-185',
      'Codified rule ids: durability.debt-created, durability.debt-repaid, durability.debt-unpaid'
    ]
  },
  {
    date: '2026-06-26',
    title: 'Ordinary non-milestone elevation is capped and redistributed.',
    summary: 'A semantic sanitizer now caps overloaded ordinary workouts without treating milestone elevation the same way.',
    details: [
      'Non-milestone workout elevation uses the configured ordinary cap, with a stricter protective-bias cap when applicable.',
      'Excess ordinary elevation is moved to safe same-week non-milestone hosts when capacity exists.',
      'Unassigned excess is reported as concentration debt instead of being hidden in a single overloaded workout.'
    ],
    sources: [
      'Commit 7f1f4532',
      'backend/Services/TrainingPlans/TrainingPlanExerciseSemanticSanitizer.cs:21-26',
      'backend/Services/TrainingPlans/TrainingPlanExerciseSemanticSanitizer.cs:88-102',
      'backend/Models/TrainingPlans/PlanGenerationConfig.cs:137-144',
      'Codified rule ids: elevation.concentration.cap, elevation.concentration.redistribute, elevation.concentration.debt-created, elevation.concentration.debt-unpaid'
    ]
  },
  {
    date: '2026-06-26',
    title: 'Protected milestone windows apply only to elevation-like milestones.',
    summary: 'The milestone window system now protects dates around elevation and user-choice milestones while leaving distance milestones out of that window.',
    details: [
      'This update originally protected three days before and one day after elevation and user-choice milestone dates; the October 20, 2026 change shortens the lead-in for the four standard elevation challenges.',
      'Distance milestones do not create protected-window dates.',
      'Protected dates are used by cleanup passes that strip nearby elevation, HIIT, and lunge stress while allowing distance-only work to remain.'
    ],
    sources: [
      'Commit b25524ff',
      'backend/Services/TrainingPlans/TrainingPlanMilestoneWindowRules.cs:24-40',
      'Codified rule ids: milestone.window.before, milestone.window.after-elevation, milestone.window.restrict-workouts'
    ]
  },
  {
    date: '2026-06-23',
    title: 'Generated distance work moved away from obsolete walk defaults.',
    summary: 'Generated distance and HIIT fallback paths now prefer run/hike variants instead of seeded walk variants.',
    details: [
      'The default exercise catalog no longer seeds the obsolete walk-only definitions.',
      'Distance-only workouts and treadmill overflow use RUN first and HIKE as fallback.',
      'Distance milestone and elevation-lead fallbacks use the current HIKE/RUN definitions.'
    ],
    sources: [
      'Commit 87099df4',
      'backend/Services/TrainingPlans/ExerciseRepository.cs:38-45',
      'backend/Services/TrainingPlans/ExerciseRepository.cs:493-581',
      'backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs:149-207',
      'backend/Services/TrainingPlans/TrainingPlanMilestonePlanner.cs:594-620'
    ]
  },
  {
    date: '2026-06-23',
    title: 'Generation traces are persisted for Base Camp and admin review.',
    summary: 'Generated plans now keep diagnostic trace entries so plan mutations can be reviewed after generation.',
    details: [
      'The generated plan stores generationTrace with rule, action, detail, date, week, and context metadata.',
      'Admin plan summaries expose plan-level trace entries.',
      'Admin week detail exposes week and workout trace entries without sending the diagnostic payload to mobile users.'
    ],
    sources: [
      'Commit 24acfc6b',
      'backend/Models/TrainingPlans/GeneratedTrainingPlan.cs:70-75',
      'backend/DTOs/Admin/AdminTrainingPlanDtos.cs:62-70',
      'backend/DTOs/Admin/AdminTrainingPlanDtos.cs:207-245'
    ]
  },
  {
    date: '2026-06-22',
    title: 'Lunge support prescriptions use explicit rep overrides.',
    summary: 'Generated lunge support now applies code-specific rep targets instead of relying on one generic fallback.',
    details: [
      'BWT3, PT3, and JT variants get explicit rep counts.',
      'PT/JT fallback codes still get a bounded default before the generic fallback.',
      'The exercise sidedness resolver was updated in the same change so generated support work carries the intended side behavior.'
    ],
    sources: [
      'Commit c1978466',
      'backend/Services/TrainingPlans/TrainingPlanExercisePrescriptionBuilder.cs:298-309',
      'backend/Services/TrainingPlans/ExerciseSidednessResolver.cs'
    ]
  }
]

export const ALL_CODIFIED_PLAN_RULES = CODIFIED_PLAN_RULE_GROUPS.flatMap(group => group.rules)

export const CODIFIED_PLAN_RULES_BY_ID = ALL_CODIFIED_PLAN_RULES.reduce<Record<string, CodifiedPlanRule>>(
  (lookup, rule) => {
    lookup[rule.id] = rule
    return lookup
  },
  {}
)

const formatMarkdownTableCell = (value: string) => value.replace(/\|/g, '\\|').replace(/\n/g, '<br>')

const appendRuleTableMarkdown = (lines: string[], table: CodifiedPlanRuleTable) => {
  lines.push('')
  lines.push(`| ${table.columns.map(formatMarkdownTableCell).join(' | ')} |`)
  lines.push(`| ${table.columns.map(() => '---').join(' | ')} |`)
  table.rows.forEach(row => {
    lines.push(`| ${row.map(formatMarkdownTableCell).join(' | ')} |`)
  })
}

export const buildCodifiedPlanRulesMarkdown = (exportedAt = new Date()) => {
  const lines = [
    '# Codified Plan Rules',
    '',
    `Exported: ${exportedAt.toISOString()}`,
    `Total rules: ${ALL_CODIFIED_PLAN_RULES.length}`,
    '',
    'This export is the complete codified ruleset used by the dashboard rules pane. Each rule has a stable id intended for generation trace lookup.',
    ''
  ]

  lines.push('## Glossary', '')
  CODIFIED_PLAN_GLOSSARY_TERMS.forEach(({ term, definition }) => {
    lines.push(`- **${term}:** ${definition}`)
  })
  lines.push('')

  lines.push('## Recent Algorithm Changes', '', 'Most recent plan-generation behavior changes, listed newest first.', '')
  PLAN_GENERATION_CHANGELOG.forEach(entry => {
    lines.push(`### ${entry.date} - ${entry.title}`, '', `**Summary:** ${entry.summary}`, '', '**Details**', '')
    entry.details.forEach(detail => {
      lines.push(`- ${detail}`)
    })

    lines.push('', '**Sources**', '')
    entry.sources.forEach(source => {
      lines.push(`- ${source}`)
    })
    lines.push('')
  })

  CODIFIED_PLAN_RULE_GROUPS.forEach(group => {
    lines.push(`## ${group.title}`, '', group.description, '')
    group.rules.forEach(rule => {
      lines.push(`### ${rule.id} - ${rule.title}`, '', `**Summary:** ${rule.summary}`, '', '**Details**', '')
      rule.details.forEach(detail => {
        lines.push(`- ${detail}`)
      })

      if (rule.table) {
        lines.push('', '**Table**')
        appendRuleTableMarkdown(lines, rule.table)
      }

      lines.push('', '**Sources**', '')
      rule.sources.forEach(source => {
        lines.push(`- ${source}`)
      })
      lines.push('')
    })
  })

  return `${lines.join('\n').trim()}\n`
}

const createRulesExportFilename = (exportedAt: Date) => {
  const stamp = exportedAt.toISOString().replace(/[:.]/g, '-')
  return `hikestrong-codified-plan-rules-${stamp}.md`
}

const downloadRulesMarkdown = () => {
  const exportedAt = new Date()
  const markdown = buildCodifiedPlanRulesMarkdown(exportedAt)
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = createRulesExportFilename(exportedAt)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}

const formatSourceLabel = (source: string) => {
  const parts = source.split('/')
  return parts[parts.length - 1] ?? source
}

type CodifiedPlanRulesPaneProps = {
  className?: string
  contentClassName?: string
  compact?: boolean
  description?: string
  title?: string
}

const RuleTable = ({ table }: { table: CodifiedPlanRuleTable }) => (
  <div className="overflow-x-auto rounded-md border border-border/70">
    <table className="min-w-full border-collapse text-left text-xs">
      <thead className="bg-muted/70 text-[11px] uppercase text-muted-foreground">
        <tr>
          {table.columns.map(column => (
            <th key={column} className="whitespace-nowrap border-b border-border/70 px-2 py-1.5 font-semibold">
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row, rowIndex) => (
          <tr key={`${row.join('|')}-${rowIndex}`} className="border-b border-border/50 last:border-b-0">
            {row.map((cell, cellIndex) => (
              <td key={`${cell}-${cellIndex}`} className="align-top px-2 py-1.5 text-muted-foreground">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

export const CodifiedPlanRulesPane = ({
  className,
  compact = false,
  contentClassName,
  description = 'Shared reference for generation and milestone behavior used by the plan tester and algorithm tools.',
  title = 'Codified Plan Rules'
}: CodifiedPlanRulesPaneProps) => (
  <Card className={cn('border-primary/10 shadow-sm', className)}>
    <CardHeader>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" aria-hidden="true" />
            <CardTitle>{title}</CardTitle>
          </div>
          <CardDescription>{description}</CardDescription>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={downloadRulesMarkdown}>
          <Download className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
          Export Markdown
        </Button>
      </div>
    </CardHeader>
    <CardContent className={cn('space-y-5', contentClassName)}>
      <details className="group rounded-lg border border-border/70 bg-muted/20">
        <summary className="flex cursor-pointer list-none items-start justify-between gap-3 px-3 py-3 [&::-webkit-details-marker]:hidden">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-foreground">Glossary</h3>
              <span className="rounded-md border border-border bg-background px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                {CODIFIED_PLAN_GLOSSARY_TERMS.length} terms
              </span>
            </div>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Shared terms used by generation traces, remediation warnings, and rule descriptions.
            </p>
          </div>
          <ChevronDown
            className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
            aria-hidden="true"
          />
        </summary>
        <div className="border-t border-border/70 px-3 pb-3 pt-2">
          <dl className={cn('grid gap-2', compact ? 'grid-cols-1' : 'sm:grid-cols-2')}>
            {CODIFIED_PLAN_GLOSSARY_TERMS.map(({ term, definition }) => (
              <div key={term} className="rounded-md border border-border/70 bg-background/70 p-3">
                <dt className="text-xs font-semibold uppercase text-foreground">{term}</dt>
                <dd className="mt-1 text-sm leading-5 text-muted-foreground">{definition}</dd>
              </div>
            ))}
          </dl>
        </div>
      </details>
      <details className="group rounded-lg border border-border/70 bg-muted/20" open>
        <summary className="flex cursor-pointer list-none items-start justify-between gap-3 px-3 py-3 [&::-webkit-details-marker]:hidden">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-foreground">Recent Algorithm Changes</h3>
              <span className="rounded-md border border-border bg-background px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                {PLAN_GENERATION_CHANGELOG.length} updates
              </span>
            </div>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Dated generation-behavior changes so rule updates do not require manual markdown comparison.
            </p>
          </div>
          <ChevronDown
            className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
            aria-hidden="true"
          />
        </summary>
        <div className="space-y-3 border-t border-border/70 px-3 pb-3 pt-3">
          {PLAN_GENERATION_CHANGELOG.map(entry => (
            <article key={`${entry.date}-${entry.title}`} className="rounded-md border border-border/70 bg-background/70 p-3">
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-md border border-primary/20 bg-primary/10 px-2 py-0.5 font-mono text-[11px] text-primary">
                    {entry.date}
                  </span>
                  <h4 className="text-sm font-semibold leading-5 text-foreground">{entry.title}</h4>
                </div>
                <p className="text-sm leading-5 text-muted-foreground">{entry.summary}</p>
              </div>
              <ul className="mt-2 space-y-1 text-sm leading-5 text-muted-foreground">
                {entry.details.map(detail => (
                  <li key={detail} className="flex gap-2">
                    <span className="mt-[0.55rem] h-1 w-1 shrink-0 rounded-full bg-muted-foreground/60" />
                    <span>{detail}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {entry.sources.map((source) => (
                  <span
                    key={source}
                    title={source}
                    className="inline-flex max-w-full items-center rounded-md border border-border bg-muted px-2 py-0.5 font-mono text-[11px] text-muted-foreground"
                  >
                    {formatSourceLabel(source)}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </details>
      {CODIFIED_PLAN_RULE_GROUPS.map((group) => (
        <section key={group.id} className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">{group.title}</h3>
            <p className="text-xs leading-5 text-muted-foreground">{group.description}</p>
          </div>
          <div className={cn('grid gap-3', compact ? 'grid-cols-1' : 'lg:grid-cols-2')}>
            {group.rules.map((rule) => (
              <article
                id={rule.id}
                key={rule.id}
                className="scroll-mt-4 rounded-lg border border-border/70 bg-background/70 p-3"
              >
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  <div className="min-w-0 space-y-2">
                    <div className="space-y-1">
                      <span className="inline-flex max-w-full items-center rounded-md border border-primary/20 bg-primary/10 px-2 py-0.5 font-mono text-[11px] text-primary">
                        {rule.id}
                      </span>
                      <h4 className="text-sm font-semibold leading-5 text-foreground">{rule.title}</h4>
                    </div>
                    <p className="text-sm leading-5 text-muted-foreground">{rule.summary}</p>
                    <ul className="space-y-1 text-sm leading-5 text-muted-foreground">
                      {rule.details.map(detail => (
                        <li key={detail} className="flex gap-2">
                          <span className="mt-[0.55rem] h-1 w-1 shrink-0 rounded-full bg-muted-foreground/60" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                    {rule.table && <RuleTable table={rule.table} />}
                    <div className="flex flex-wrap gap-1.5">
                      {rule.sources.map((source) => (
                        <span
                          key={source}
                          title={source}
                          className="inline-flex max-w-full items-center rounded-md border border-border bg-muted px-2 py-0.5 font-mono text-[11px] text-muted-foreground"
                        >
                          {formatSourceLabel(source)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}
    </CardContent>
  </Card>
)
